import SafeMJ from "../../utils/SafeMJ";

import qr_anatomy from "../assets/qr_anatomy_v2.en.png";
import qr_zigzag from "../assets/qr_v2_zigzag.en.png";

function Proof({ children }) {
  return (
    <div className="border-l-4 border-gray-300 dark:border-gray-700 pl-4 mb-3">
      {children}
    </div>
  );
}

export default function ReedSolomonEn() {
  // Matrices reales 25x25 del QR de https://jaimegimillo.me, versión 2, nivel M,
  // con la máscara 6. Ambas escanean correctamente a la misma URL: la segunda
  // tiene 8 bytes ENTEROS (todos sus bits) puestos a negro —los números 15, 16,
  // 21, 22, 27, 28, 33 y 34—, elegidos para que, al estar en columnas vecinas
  // a la misma altura, formen una mancha compacta en el centro (filas 8-17,
  // columnas 9-16), justo el máximo t=8 corregible.
  const QR_ORIG = `1111111010001010001111111
1000001010000100101000001
1011101010111101101011101
1011101001001101101011101
1011101010100110101011101
1000001000000010001000001
1111111010101010101111111
0000000001111110100000000
1001111111010010110010111
0101110110011111000111110
1100101011001101111001001
0110110101010010101011111
1001101100101010101100001
1011100111000011100010010
1100111110111011000011111
1001110110110011011101101
1011101110000101111110110
0000000010111100100010110
1111111011010100101010001
1000001010001101100010010
1011101010001011111110011
1011101010000011111000011
1011101000101000010011111
1000001001000010000110111
1111111010101110101001001`.split("\n");
  const QR_DMG = `1111111010001010001111111
1000001010000100101000001
1011101010111101101011101
1011101001001101101011101
1011101010100110101011101
1000001000000010001000001
1111111010101010101111111
0000000001111110100000000
1001111111011010110010111
0101110111011111100111110
1100101011111111111001001
0110110101111111101011111
1001101101111111101100001
1011100111111111100010010
1100111111111111100011111
1001110111111111111101101
1011101111110111111110110
0000000010111110100010110
1111111011010100101010001
1000001010001101100010010
1011101010001011111110011
1011101010000011111000011
1011101000101000010011111
1000001001000010000110111
1111111010101110101001001`.split("\n");
  // 1 = este módulo concreto cambió de valor respecto al original por culpa
  // de la mancha (se pinta en rojo, sea cual sea su color final).
  const QR_DIFF = `0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000001000000000000
0000000001000000100000000
0000000000110010000000000
0000000000101101000000000
0000000001010101000000000
0000000000111100000000000
0000000001000100100000000
0000000001001100100000000
0000000001110010000000000
0000000000000010000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000`.split("\n");
    // 9 bytes manchados: 15, 16, 21, 22, 27, 28, 32, 33, 34 (72 bits).
  // Sigue sin decodificar (9 > t=8).
  const QR_DMG_9 = `1111111010001010001111111
1000001010000100101000001
1011101010111101101011101
1011101001001101101011101
1011101011100110101011101
1000001001100010001000001
1111111010101010101111111
0000000001111110100000000
1001111111111010110010111
0101110111111111100111110
1100101011111111111001001
0110110101111111101011111
1001101101111111101100001
1011100111111111100010010
1100111111111111100011111
1001110111111111111101101
1011101111110111111110110
0000000010111110100010110
1111111011010100101010001
1000001010001101100010010
1011101010001011111110011
1011101010000011111000011
1011101000101000010011111
1000001001000010000110111
1111111010101110101001001`.split("\n");
  const QR_DIFF_9 = `0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000001000000000000000
0000000001100000000000000
0000000000000000000000000
0000000001100000000000000
0000000001101000100000000
0000000001111101100000000
0000000001111111100000000
0000000001111111100000000
0000000001111111100000000
0000000001111111100000000
0000000001111111100000000
0000000001111111100000000
0000000001110111000000000
0000000000100010000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000`.split("\n");

  const renderQR = (rows, caption, label, diffRows = null) => {
    const N = rows.length;
    const cell = 8;
    const quiet = 4;
    const size = (N + 2 * quiet) * cell;
    const rects = [];
    rows.forEach((row, r) => {
      [...row].forEach((ch, c) => {
        const isDiff = diffRows && diffRows[r][c] === "1";
        if (ch === "1" || isDiff) {
          rects.push(
            <rect
              key={`${r}-${c}`}
              x={(c + quiet) * cell}
              y={(r + quiet) * cell}
              width={cell}
              height={cell}
              fill={isDiff ? "#000" : "#000"}
            />
          );
        }
      });
    });
    return (
      <figure className="flex flex-col items-center m-0">
        <svg viewBox={`0 0 ${size} ${size}`} className="w-60 h-60 sm:w-64 sm:h-64 rounded-md shadow" shapeRendering="crispEdges" role="img" aria-label={label}>
          <rect x="0" y="0" width={size} height={size} fill="#fff" />
          {rects}
        </svg>
        <figcaption className="mt-3 max-w-[15rem] sm:max-w-[16rem] text-base text-gray-600 dark:text-gray-400 text-center">{caption}</figcaption>
      </figure>
    );
  };

  return (
    <>
    <p className="mb-3">
      Who didn't get a PlayStation game a bit scratched as a kid? Blame it on your little brother,
      on the blasted cat, or on the good old "I'll just leave it out of the case for a sec"...
      And yet, any kid who grew up with consoles can confirm that the game, with a bit of luck, could still keep working.
      Mind you, I guarantee it wasn't because you rubbed the shiny side of the disc with a cloth, nor because of the spit trick your
      cousin told you about. You actually owe the miracle to two guys: Irving S. Reed and Gustave Solomon, and to the error-correction method they developed in the 60s.
    </p>
    <p className="mb-3">
      Today we'll therefore focus on a particular case of <b>coding theory</b>, which, as you know, studies methods for transmitting information efficiently and resiliently, that is,
      in such a way that the receiver can recover the original message even if errors or alterations have occurred along the way. Note that sending and receiving could just as well be writing and reading,
      or recording and playing back.
    </p>
    <p className="mb-3">
      And although the example above was a bit silly, Reed-Solomon codes have in fact had very serious applications ever since they were created. I'll just say
      that until the end of the 20th century, they were the go-to method for NASA's space communications, so no joke.
    </p>
    <p className="mb-3">
      Nowadays, there are more modern and sophisticated coding methods, obviously, but Reed-Solomon is still a very widespread standard thanks to its simplicity and efficiency when not handling large volumes of data.
      Possibly, its best-known current application is QR codes. With a small stain, a torn corner or poor lighting, your phone can still scan them perfectly, and that's thanks to Reed-Solomon and,
      if we get romantic, to finite fields and Galois theory. But let's not get ahead of ourselves, and let's go step by step.
    </p>
    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">1. A brief review of finite fields</h2>
    <p className="mb-3">
      All the Reed-Solomon machinery lives over a <em>finite field</em>, so allow me to spend a few paragraphs recalling this concept. If you have no idea about abstract algebra, first of all: I'm sorry. I, at least, remember it
      as one of the toughest modules of my degree. I invite you to do a bit of research on your own before reading on or, failing that, to simply stick with the most important ideas of this section.
    </p>
    <p className="mb-3">
      With that said, let's start with the basics. You already know that a <b>field</b> is a triple <SafeMJ>{"\\((K,+,\\cdot)\\)"}</SafeMJ> where:
      <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1"><SafeMJ>{"\\((K,+)\\)"}</SafeMJ> is an abelian group with identity <SafeMJ after=".">{"\\(0\\)"}</SafeMJ></li>
        <li className="mb-1"><SafeMJ>{"\\((K\\setminus\\{0\\},\\cdot)\\)"}</SafeMJ> is an abelian group with identity <SafeMJ after=".">{"\\(1\\)"}</SafeMJ></li>
        <li className="mb-1">Multiplication is distributive over addition.</li>
      </ul>
      Basically, you can do the good old operations (add, subtract, multiply and divide) and you'll always get a result inside the field. For example, <SafeMJ after=",">{"\\(\\mathbb{Q}\\)"}</SafeMJ> <SafeMJ>{"\\(\\mathbb{R}\\)"}</SafeMJ> and <SafeMJ>{"\\(\\mathbb{C}\\)"}</SafeMJ> are fields, but <SafeMJ>{"\\(\\mathbb{Z}\\)"}</SafeMJ>{" "}
      isn't, since only <SafeMJ>{"\\(1\\)"}</SafeMJ> has a multiplicative inverse. So, a <b>finite field</b> is simply a field with a finite number of elements. Easy so far.
    </p>
    <p className="mb-3">
      Recall also that the <b>characteristic</b> of a field <SafeMJ after=",">{"\\(K\\)"}</SafeMJ> denoted <SafeMJ after=",">{"\\(\\mathrm{char}(K)\\)"}</SafeMJ> is defined as the smallest <SafeMJ>{"\\(n\\in\\mathbb{N}\\)"}</SafeMJ> such that:
      <SafeMJ scrollable>{"\\[\\underbrace{1+1+\\cdots+1}_{n}=0.\\]"}</SafeMJ>
      That is, if such an <SafeMJ>{"\\(n\\)"}</SafeMJ> exists, since otherwise <SafeMJ>{"\\(K\\)"}</SafeMJ> is said to have characteristic <SafeMJ after=".">{"\\(0\\)"}</SafeMJ>
    </p>
    <p className="mb-5">
      For example, if we start adding <SafeMJ after="'s">{"\\(1\\)"}</SafeMJ> in <SafeMJ after=",">{"\\(\\mathbb{Q}\\)"}</SafeMJ> we'll never reach <SafeMJ after=",">{"\\(0\\)"}</SafeMJ> so <SafeMJ after=".">{"\\(\\mathrm{char}(\\mathbb{Q})=0\\)"}</SafeMJ>{" "}
      In contrast, the field of integers modulo a prime <SafeMJ after=":">{"\\(p\\)"}</SafeMJ>
      <SafeMJ scrollable>{"\\[\\mathbb{F}_p=\\mathbb{Z}/p\\mathbb{Z}=\\{0,1,\\ldots,p-1\\},\\]"}</SafeMJ>
      has characteristic exactly <SafeMJ after=",">{"\\(p\\)"}</SafeMJ> since in this field
      <SafeMJ scrollable>{"\\[\\underbrace{1+1+\\cdots+1}_{p}=p\\equiv 0\\pmod p.\\]"}</SafeMJ>
      You already know that in this kind of congruence ring, integers behave like the hands of a clock with <SafeMJ>{"\\(p\\)"}</SafeMJ> hours.
      So <SafeMJ after=",">{"\\(-2p\\)"}</SafeMJ> <SafeMJ after=",">{"\\(-p\\)"}</SafeMJ> <SafeMJ after=",">{"\\(0\\)"}</SafeMJ> <SafeMJ after=",">{"\\(p\\)"}</SafeMJ> <SafeMJ after=",">{"\\(2p\\)"}</SafeMJ> ... are all equivalent, since they all leave
      remainder <SafeMJ>{"\\(0\\)"}</SafeMJ> when divided by <SafeMJ after=".">{"\\(p\\)"}</SafeMJ>{" "}
      Moreover, the fact that we required <SafeMJ>{"\\(p\\)"}</SafeMJ> to be prime is key for <SafeMJ>{"\\(\\mathbb{F}_p\\)"}</SafeMJ> to really be a field, since thanks to Bézout's identity, every element <SafeMJ>{"\\(a\\neq 0\\)"}</SafeMJ> has
      a multiplicative inverse:
      <SafeMJ scrollable>{"\\[\\exists x,y\\in\\mathbb{Z}:\\;1=\\gcd{(a,p)}=ax+py=ax\\Longrightarrow a^{-1}=x.\\]"}</SafeMJ>
      Look, for instance, at the case of <SafeMJ after=":">{"\\(\\mathbb{F}_7\\)"}</SafeMJ>
    </p>
    <div className="overflow-x-auto md:overflow-visible overflow-y-hidden max-w-full mb-5">
      <table className="table-auto border-collapse border border-gray-300 dark:border-gray-700 w-full text-center">
        <tbody className="bg-white dark:bg-gray-900">
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2 bg-gray-100 dark:bg-gray-800"><SafeMJ>{"\\(a\\)"}</SafeMJ></th>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(0\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(3\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(4\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(5\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(6\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2 bg-gray-100 dark:bg-gray-800"><SafeMJ>{"\\(-a\\)"}</SafeMJ></th>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(0\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(6\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(5\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(4\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(3\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(1\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2 bg-gray-100 dark:bg-gray-800"><SafeMJ>{"\\(a^{-1}\\)"}</SafeMJ></th>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(-\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(4\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(5\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(3\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(6\\)"}</SafeMJ></td>
          </tr>
        </tbody>
      </table>
    </div>
    <p className="mb-3">
      In fact, the above was no coincidence since, as the following proposition shows, prime numbers impose
      a very particular necessary condition on finite fields.
    </p>

    <p className="mb-3"> <b>Proposition 1.1.</b> The characteristic of a field is always zero or a prime number <SafeMJ after=".">{"\\(p\\)"}</SafeMJ>{" "}
    Moreover, if the field is finite, then its characteristic is non-zero and
    its cardinality is <SafeMJ after=",">{"\\(p^n\\)"}</SafeMJ> for some <SafeMJ after=".">{"\\(n\\in\\mathbb{N}\\)"}</SafeMJ>
    </p>

    <Proof>
    <p className="mb-3">
      <em>Proof.</em> Let <SafeMJ>{"\\(K\\)"}</SafeMJ> be a field with <SafeMJ after=",">{"\\(\\mathrm{char}(K)=p\\neq 0\\)"}</SafeMJ> then <SafeMJ>{"\\(p\\)"}</SafeMJ> must be prime, since otherwise we would have <SafeMJ>{"\\(p=ab\\)"}</SafeMJ> with <SafeMJ after=",">{"\\(1<a,b<p\\)"}</SafeMJ> and by distributivity:
      <SafeMJ scrollable>{"\\[0=\\underbrace{1+\\cdots+1}_{p}=(\\underbrace{1+\\cdots+1}_{a})\\cdot(\\underbrace{1+\\cdots+1}_{b})\\Longrightarrow 0=a^{-1}ab=b\\;!!!\\]"}</SafeMJ>
      In particular, if <SafeMJ>{"\\(K\\)"}</SafeMJ> is finite, <SafeMJ>{"\\(\\mathrm{char}(K)=0\\)"}</SafeMJ> cannot happen, since in that case <SafeMJ>{"\\(K\\)"}</SafeMJ> would contain infinitely many elements (one for each new <SafeMJ>{"\\(1\\)"}</SafeMJ> we
      add). Also, note that the subfield <SafeMJ>{"\\(\\mathbb{F}_p\\subset K\\)"}</SafeMJ> generated by <SafeMJ>{"\\(1\\)"}</SafeMJ> makes <SafeMJ>{"\\(K\\)"}</SafeMJ> an <SafeMJ after="vector">{"\\(\\mathbb{F}_p\\text{-}\\)"}</SafeMJ> space (this is what's called a
      field extension, by the way). Being finite, it has finite dimension <SafeMJ after=",">{"\\(n\\in\\mathbb{N}\\)"}</SafeMJ> so <SafeMJ after=".">{"\\(|K|=|\\mathbb{F}_p|^{\\dim{K}}=p^n\\)"}</SafeMJ><span className="qed"><SafeMJ>{"\\(\\square\\)"}</SafeMJ></span>
    </p>
    </Proof>

    <p className="mb-3">
      There are also infinite fields with prime characteristic, although the converse doesn't hold, as we already saw with <SafeMJ after=".">{"\\(\\mathbb{Q}\\)"}</SafeMJ> For example,
      there's the classic <SafeMJ after=",">{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ> made up of the infinitely many polynomials with coefficients in <SafeMJ after=",">{"\\(\\mathbb{F}_p\\)"}</SafeMJ> which,
      since we're still adding <SafeMJ after="'s">{"\\(1\\)"}</SafeMJ> in <SafeMJ after=",">{"\\(\\mathbb{F}_p\\)"}</SafeMJ> also has characteristic <SafeMJ after=".">{"\\(p\\)"}</SafeMJ>
    </p>

    <p className="mb-3">
        Very good, very interesting. However, what we're really interested in is sort of the reverse process:
        given a prime <SafeMJ>{"\\(p\\)"}</SafeMJ> and a natural number <SafeMJ after=",">{"\\(n\\)"}</SafeMJ> how do we build a finite field
        with <SafeMJ>{"\\(p^n\\)"}</SafeMJ> elements? For that we have, luckily, the following proposition:
    </p>
    <p className="mb-3">
      <b>Proposition 1.2.</b> Let <SafeMJ>{"\\(f(x)\\in\\mathbb{F}_p[x]\\)"}</SafeMJ> be an irreducible polynomial of degree <SafeMJ after=",">{"\\(n\\)"}</SafeMJ> then the quotient ring
      <SafeMJ scrollable>{"\\[\\mathbb{F}_p[x]\\big/\\langle f(x)\\rangle\\]"}</SafeMJ>
      is a field with exactly <SafeMJ>{"\\(p^n\\)"}</SafeMJ> elements.
    </p>
    <p className="mb-3">
      To refresh your memory, a non-constant polynomial <SafeMJ>{"\\(f(x)\\in\\mathbb{F}_p[x]\\)"}</SafeMJ> is said to be <b>irreducible</b> when it can't be expressed as
      a product of two non-constant polynomials of <SafeMJ after=".">{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ> For example, <SafeMJ>{"\\(x^2+1\\)"}</SafeMJ> is irreducible in <SafeMJ after=",">{"\\(\\mathbb{F}_3[x]\\)"}</SafeMJ>{" "}
      since it has no roots in that field (<SafeMJ after=",">{"\\(f(0)=1\\)"}</SafeMJ> <SafeMJ>{"\\(f(1)=2\\)"}</SafeMJ> and <SafeMJ after=")">{"\\(f(2)=2\\)"}</SafeMJ> and, therefore, it can't be factorised as a product of monomials.
      On the other hand, that very same polynomial is reducible in <SafeMJ after=",">{"\\(\\mathbb{F}_5[x]\\)"}</SafeMJ> since it can be decomposed as <SafeMJ after=".">{"\\(x^2+1=(x+2)(x+3)\\)"}</SafeMJ>
    </p>
    <p className="mb-3">
      Also, recall that quotient rings are built as follows. You take a ring <SafeMJ after=",">{"\\(R\\)"}</SafeMJ> in our case <SafeMJ after=",">{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ>{" "}
      and an ideal <SafeMJ>{"\\(I\\)"}</SafeMJ> of it, that is, an additive subgroup of <SafeMJ>{"\\(R\\)"}</SafeMJ> that is closed under multiplication by elements of the ring.
      In our case, that special subgroup is <SafeMJ after=",">{"\\(\\langle f(x)\\rangle\\)"}</SafeMJ> that is, all the polynomials divisible by <SafeMJ after=".">{"\\(f(x)\\)"}</SafeMJ>{" "}
      Then, the set <SafeMJ>{"\\(R/I\\)"}</SafeMJ> of equivalence classes <SafeMJ>{"\\(a+I\\)"}</SafeMJ> with <SafeMJ after=",">{"\\(a\\in R\\)"}</SafeMJ> where:
      <SafeMJ scrollable>{"\\[a+I=[a]:=\\{b\\in R:a-b\\in I\\},\\]"}</SafeMJ>
      turns out to be (I encourage you to check it) a ring with the following operations:
      <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1"><SafeMJ>{"\\((a+I)+(b+I):=(a+b)+I,\\)"}</SafeMJ></li>
        <li className="mb-1"><SafeMJ>{"\\((a+I)\\cdot (b+I):=(a\\cdot b)+I.\\)"}</SafeMJ></li>
      </ul>
      It looks much messier than it is. Actually, feel free to forget all this notation and think of <SafeMJ>{"\\(\\mathbb{F}_p[x]/\\langle f(x)\\rangle\\)"}</SafeMJ> as
      if they were ordinary polynomials of <SafeMJ after=",">{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ> except that if the difference of two of them is divisible by <SafeMJ after=",">{"\\(f(x)\\)"}</SafeMJ> then you say
      those polynomials are equal.
    </p>
    <p className="mb-3">
      Right. With that cleared up, let's now see the proof of the previous proposition.
    </p>
    <Proof>
    <p className="mb-3">
      <em>Proof.</em> The fact that <SafeMJ>{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ> is a commutative ring automatically makes the quotient ring <SafeMJ>{"\\(K=\\mathbb{F}_p[x]\\big/\\langle f(x)\\rangle\\)"}</SafeMJ>{" "}
      commutative too. Therefore, for <SafeMJ>{"\\(K\\)"}</SafeMJ> to be a field, we only need to see that every non-zero element has a multiplicative inverse.
    </p>
    <p className="mb-3">
      Indeed, let <SafeMJ>{"\\(a(x)\\in\\mathbb{F}_p[x]\\)"}</SafeMJ> be non-zero with <SafeMJ after=",">{"\\(\\deg(a)<n=\\deg(f)\\)"}</SafeMJ> then <SafeMJ>{"\\(f\\nmid a\\)"}</SafeMJ> simply because of the degrees.
      Furthermore, since <SafeMJ>{"\\(f(x)\\)"}</SafeMJ> is irreducible, it has no divisor with degree between <SafeMJ>{"\\(1\\)"}</SafeMJ> and <SafeMJ after=",">{"\\(n-1\\)"}</SafeMJ> so <SafeMJ after=".">{"\\(1\\in\\gcd(f,a)\\)"}</SafeMJ>{" "}
      Then, since <SafeMJ>{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ> is a Euclidean domain, we can apply Bézout's identity to get <SafeMJ>{"\\(u(x),v(x)\\in\\mathbb{F}_p[x]\\)"}</SafeMJ> such that:
      <SafeMJ scrollable>{"\\[a(x)\\,u(x)+f(x)\\,v(x)=1.\\]"}</SafeMJ>
      Now passing to the quotient, we get <SafeMJ after=",">{"\\([a(x)][u(x)]=[1]\\)"}</SafeMJ> so <SafeMJ after=".">{"\\([a(x)]^{-1}=[u(x)]\\)"}</SafeMJ>
    </p>
    <p className="mb-3">
      Note, moreover, that if <SafeMJ after=",">{"\\(\\deg(a)\\ge n\\)"}</SafeMJ> we simply apply Euclidean division by <SafeMJ>{"\\(f(x)\\)"}</SafeMJ> to get:
      <SafeMJ scrollable>{"\\[a(x)=c(x)f(x)+r(x),\\quad\\textnormal{with }\\deg(r)<n,\\]"}</SafeMJ>
      so that <SafeMJ after=",">{"\\([a(x)]=[r(x)]\\)"}</SafeMJ> bringing us back to the previous case. In fact, this last point proves that each class <SafeMJ>{"\\([a(x)]\\)"}</SafeMJ> has a unique representative of
      degree less than <SafeMJ after=".">{"\\(n\\)"}</SafeMJ> Therefore, <SafeMJ>{"\\(|K|\\)"}</SafeMJ> will be the number of polynomials in <SafeMJ>{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ> of
      degree less than <SafeMJ after=",">{"\\(n\\)"}</SafeMJ> which is exactly <SafeMJ>{"\\(p^n\\)"}</SafeMJ> (one for each choice of the <SafeMJ>{"\\(n\\)"}</SafeMJ> coefficients in <SafeMJ after=").">{"\\(\\mathbb{F}_p\\)"}</SafeMJ>
      <span className="qed"><SafeMJ>{"\\(\\square\\)"}</SafeMJ></span>
    </p>
    </Proof>

    <p className="mb-3">
      Before moving on, it might help to see a particular example of all this. I'm sure someone will thank me for it... Let's consider, then,
      the irreducible polynomial from before, <SafeMJ after=".">{"\\(f(x)=x^2+1\\in\\mathbb{F}_3[x]\\)"}</SafeMJ>{" "}
      We now know that we have the field <SafeMJ after=",">{"\\(\\mathbb{F}_3[x]/\\langle x^2+1\\rangle\\)"}</SafeMJ>{" "}
      whose <SafeMJ>{"\\(3^2=9\\)"}</SafeMJ> elements are:
    </p>
    <div className="overflow-x-auto md:overflow-visible overflow-y-hidden max-w-full mb-4">
      <table className="table-auto sm:table-fixed border-collapse border border-gray-300 dark:border-gray-700 w-full text-center">
        <tbody className="bg-white dark:bg-gray-900">
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2 bg-gray-100 dark:bg-gray-800"><SafeMJ>{"\\(a(x)\\)"}</SafeMJ></th>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(0\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x+1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x+2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x+1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x+2\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2 bg-gray-100 dark:bg-gray-800"><SafeMJ>{"\\(-a(x)\\)"}</SafeMJ></th>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(0\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x+2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x+1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x+2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x+1\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2 bg-gray-100 dark:bg-gray-800"><SafeMJ>{"\\(a(x)^{-1}\\)"}</SafeMJ></th>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(-\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x+2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x+1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x+2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x+1\\)"}</SafeMJ></td>
          </tr>
        </tbody>
      </table>
    </div>
    <p className="mb-3">
      For example, why is the inverse of <SafeMJ>{"\\(2x+1\\)"}</SafeMJ> equal to <SafeMJ after="?">{"\\(2x+2\\)"}</SafeMJ> The key is, as we explained before, that all
      multiples of <SafeMJ>{"\\(f(x)\\)"}</SafeMJ> vanish in the quotient ring, so that:
      <SafeMJ scrollable>{"\\[(2x+1)(2x+2)=4x^2+2=4(\\underbrace{x^2+1}_{\\displaystyle 0})+1=1.\\]"}</SafeMJ>
      Note that I've left out the square brackets denoting the equivalence classes. In practice nobody writes them, since they're implied and it
      simplifies the notation.
    </p>

    <p className="mb-3">
      Great, so we now know how to build fields with cardinality <SafeMJ after=".">{"\\(p^n\\)"}</SafeMJ> It wasn't that hard in the end... However,
      it would be a rookie maths mistake to stop here, since there are still many natural questions to answer. For example, we've seen that,
      to build a field with <SafeMJ>{"\\(p^n\\)"}</SafeMJ> elements, all we need is an irreducible polynomial of
      degree <SafeMJ>{"\\(n\\)"}</SafeMJ> in <SafeMJ after=",">{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ> but nobody has told us that we can always
      find such a polynomial. Nor do we know whether, if there are several candidate irreducible polynomials, we would end up
      getting different fields or, on the contrary, they would be "essentially" the same (i.e., a matter of renaming variables).
    </p>
    <p className="mb-3">
      The answer to these questions is given by the brilliant Galois Theory (hats off to Évariste!), which, unfortunately, we don't have time to delve into in this article.
      If you're up for it and get a bit familiar with so-called splitting fields, Euler's totient function and the Frobenius automorphism,
      you'll see it's not hard to prove the following:
      <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1">For every prime <SafeMJ>{"\\(p\\)"}</SafeMJ> and every natural number <SafeMJ after=",">{"\\(n\\)"}</SafeMJ> there exists an irreducible polynomial
        of degree <SafeMJ>{"\\(n\\)"}</SafeMJ> in <SafeMJ after=",">{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ> so <b>we can always build
        fields with <SafeMJ>{"\\(\\boldsymbol{p^n}\\)"}</SafeMJ> elements</b>.
        </li>
        <li className="mb-1">
          The field with <SafeMJ>{"\\(q=p^n\\)"}</SafeMJ> elements <b>is unique up to isomorphism</b>, and we denote it by <SafeMJ>{"\\(\\mathbb{F}_{q}\\)"}</SafeMJ> or <SafeMJ after=".">{"\\(\\mathrm{GF}(q)\\)"}</SafeMJ>{" "}
          This field is actually called a <b>Galois field</b>.
        </li>
        <li className="mb-1">
          Moreover, the multiplicative group <SafeMJ>{"\\(\\mathbb{F}_{q}^{\\times}=\\mathbb{F}_{q}\\setminus\\{0\\}\\)"}</SafeMJ> turns out to be cyclic. That is,
          all its elements are powers of a certain <em>primitive element</em> <SafeMJ after=".">{"\\(\\alpha\\in\\mathbb{F}_{q}^{\\times}\\)"}</SafeMJ>
        </li>
      </ul>
      Pretty powerful, to be honest, and we haven't even talked about the relationship with the roots of <SafeMJ after=".">{"\\(f(x)\\)"}</SafeMJ> That's a whole other post...
    </p>
    <p className="mb-3">
      So, going back to the example from before, Galois Theory guarantees that <SafeMJ>{"\\(\\mathbb{F}_9\\cong \\mathbb{F}_3[x]/\\langle x^2+1\\rangle\\)"}</SafeMJ> is,
      basically, the only field with 9 elements there is. What's more, it turns out that <SafeMJ>{"\\(\\alpha=x+1\\)"}</SafeMJ> generates all of it, being a primitive element of order 8:
      <SafeMJ scrollable>{`
        \\[
        \\begin{aligned}
        \\alpha^2 &= (x+1)^2=(x^2+1)+2x=2x \\\\
        \\alpha^3 &= \\alpha^2\\cdot \\alpha=2x(x+1)=2(x^2+1)+2x+1=2x+1 \\\\
        \\alpha^4 &= (\\alpha^2)^2=4x^2=(x^2+1)+2=2 \\\\
        \\alpha^5 &= \\alpha^4\\cdot \\alpha=2x+2 \\\\
        \\alpha^6 &= \\alpha^4\\cdot \\alpha^2=4x=x \\\\
        \\alpha^7 &= \\alpha^4\\cdot \\alpha^3=4x+2=x+2 \\\\
        \\alpha^8 &= (\\alpha^4)^2=4=1.
        \\end{aligned}
        \\]
      `}
      </SafeMJ>
      Done. It's been a tad dense (apologies), but we now know everything we need about finite fields to confidently tackle Reed-Solomon
      codes, the true stars of today's show. So stretch your back and take a seat, because now we're really... starting!
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">2. The Reed-Solomon code</h2>

    <p className="mb-3">
      To understand Reed-Solomon, first of all we need to define what a (<SafeMJ after="ary)">{"\\(q\\text{-}\\)"}</SafeMJ> <b>code</b> of length <SafeMJ>{"\\(n\\in\\mathbb{N}\\)"}</SafeMJ> is, which is nothing more and nothing less than any
      subset <SafeMJ after=".">{"\\(\\mathcal{C}\\subset\\mathbb{F}_q^n\\)"}</SafeMJ> If, in addition, <SafeMJ>{"\\(\\mathcal{C}\\)"}</SafeMJ> is a vector subspace of <SafeMJ after=",">{"\\(\\mathbb{F}_q^n\\)"}</SafeMJ> then the code is said to be <b>linear</b>.
      Note that this definition makes sense since, by the properties of fields, <SafeMJ>{"\\(\\mathbb{F}_q^n\\)"}</SafeMJ> is, naturally, a vector space over the finite field <SafeMJ after=".">{"\\(\\mathbb{F}_q\\)"}</SafeMJ>
    </p>
    
    <p className="mb-3">
      On the other hand, we often refer to the vectors of <SafeMJ>{"\\(\\mathbb{F}_q^n\\)"}</SafeMJ> as <em>words</em>, while the <em>dimension</em> of a linear code is,
      of course, the good old dimension of the subspace.
    </p>

    <p className="mb-3">
      Mini-example: <SafeMJ>{"\\(\\mathcal{C}=\\{(0,0,0),(1,0,1)\\}\\subset\\mathbb{F}_2^3\\)"}</SafeMJ> is a binary linear code of length 3 and dimension 1,
      since the 3 coordinates of each word are <SafeMJ>{"\\(0\\)"}</SafeMJ> or <SafeMJ>{"\\(1\\)"}</SafeMJ> and taking the word <SafeMJ>{"\\((1,0,1)\\)"}</SafeMJ> is enough to generate it.
    </p>

    <p className="mb-3">
      As you can imagine, there are loads of ways to produce linear codes. The one we're interested in today, which gives rise to the <b>Reed-Solomon code</b>, is based, in particular,
      on playing with polynomials over finite fields. The idea is as follows:
      <ul className="list-decimal list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1 marker:font-bold">We choose a finite field <SafeMJ>{"\\(\\mathbb{F}_q\\)"}</SafeMJ> and two natural numbers <SafeMJ after="(message">{"\\(k\\,\\)"}</SafeMJ> length) and <SafeMJ after="(codeword">{"\\(n\\,\\)"}</SafeMJ> length)
        satisfying <SafeMJ after=".">{"\\(k\\le n\\le q\\)"}</SafeMJ></li>
        <li className="mb-1 marker:font-bold">We also choose <SafeMJ>{"\\(n\\)"}</SafeMJ> distinct points <SafeMJ after=".">{"\\(\\alpha_1,\\dots,\\alpha_n\\in\\mathbb{F}_q\\)"}</SafeMJ> Any will do.</li>
        <li className="mb-1 marker:font-bold">To each message <SafeMJ>{"\\(m=(m_0,\\dots,m_{k-1})\\in\\mathbb{F}_q^k\\)"}</SafeMJ> we want to transmit we associate the polynomial <SafeMJ after=".">{"\\(m(x)=\\sum_{i=0}^{k-1}m_i x^i\\)"}</SafeMJ></li>
        <li className="mb-1 marker:font-bold">Finally, we evaluate said polynomial at the <SafeMJ>{"\\(n\\)"}</SafeMJ> chosen points, obtaining the
        codeword <SafeMJ after=".">{"\\(c=(m(\\alpha_1),...,m(\\alpha_n))\\in\\mathbb{F}_q^n\\)"}</SafeMJ></li>
      </ul>
    </p>
    <p className="mb-3">
      Therefore, we formally define the Reed-Solomon code as:
      <SafeMJ scrollable>{"\\[\\mathrm{RS}(n,k):=\\bigl\\{(m(\\alpha_1),\\dots,m(\\alpha_n)):\\,m(x)\\in\\mathbb{F}_q[x]_{<k}\\bigr\\}\\subset\\mathbb{F}_q^n,\\]"}</SafeMJ>
      where <SafeMJ>{"\\(\\mathbb{F}_q[x]_{<k}\\)"}</SafeMJ> denotes the polynomials in <SafeMJ>{"\\(\\mathbb{F}_q[x]\\)"}</SafeMJ> of degree less than <SafeMJ after=".">{"\\(k\\)"}</SafeMJ>
    </p>
    <p className="mb-3">
      Careful! The restriction <SafeMJ>{"\\(n\\le q\\)"}</SafeMJ> is key, since we need to choose <SafeMJ>{"\\(n\\)"}</SafeMJ> distinct points in <SafeMJ after=",">{"\\(\\mathbb{F}_q\\)"}</SafeMJ> which only has <SafeMJ>{"\\(q\\)"}</SafeMJ> elements.
      Likewise, it's essential that <SafeMJ after=",">{"\\(k\\le n\\)"}</SafeMJ> since this will guarantee, as we'll see later on, that each message is encoded into a different word.
    </p>

    <p className="mb-3">
        Let's look at a silly example, to consolidate concepts. Imagine we choose the field <SafeMJ after=",">{"\\(\\mathbb{F}_5\\)"}</SafeMJ> assuming we want to send messages with vowels only, so
        that <SafeMJ after=",">{"\\(a=0\\)"}</SafeMJ> <SafeMJ after=",">{"\\(e=1\\)"}</SafeMJ> <SafeMJ after=",">{"\\(i=2\\)"}</SafeMJ> <SafeMJ>{"\\(o=3\\)"}</SafeMJ> and <SafeMJ after=".">{"\\(u=4\\)"}</SafeMJ>{" "}
        We also take <SafeMJ>{"\\(k=3\\)"}</SafeMJ> and <SafeMJ>{"\\(n=5\\)"}</SafeMJ> and choose the points <SafeMJ>{"\\(\\alpha_i=i-1\\)"}</SafeMJ> for <SafeMJ after=".">{"\\(i=1,2,3,4,5\\)"}</SafeMJ>{" "}
        Then, if we wanted to transmit the message <SafeMJ after=",">{"\\(\\textrm{`}ouo\\textrm{'}\\)"}</SafeMJ> the encoding process would be:
        <SafeMJ scrollable>{`
          \\[
          \\begin{gathered}
          m=(o,u,o)=(3,4,3)\\longrightarrow m(x)=3+4x+3x^2 \\\\[5pt]
          \\longrightarrow c=(m(0),m(1),m(2),m(3),m(4))=(3,10,23,42,67)\\equiv(3,0,3,2,2).
          \\end{gathered}
          \\]
        `}</SafeMJ>
    </p>
    <p className="mb-3">
      Great, that makes things clear. However, there's a detail we've skipped. We've said that the Reed-Solomon code is an example of a linear code, but we haven't proved that it is, that is,
      we still need to prove the following statement:
    </p>
    <p className="mb-3">
      <b>Proposition 2.1.</b> <SafeMJ>{"\\(\\mathrm{RS}(n,k)\\)"}</SafeMJ> is an <SafeMJ after="vector">{"\\(\\mathbb{F}_q\\text{-}\\)"}</SafeMJ> subspace of <SafeMJ>{"\\(\\mathbb{F}_q^n\\)"}</SafeMJ> of dimension <SafeMJ after=".">{"\\(k\\)"}</SafeMJ>
    </p>
    
    <p className="mb-3">
      For that, however, we need a couple of fairly elementary preliminary results about polynomials. To do things properly,
      let's see them over an arbitrary field <SafeMJ after=",">{"\\(K\\)"}</SafeMJ> since generalising never hurts. Here's the first one:
    </p>

    <p className="mb-3">
    <b>Lemma 2.2.</b> Let <SafeMJ>{"\\(m(x)\\in K[x]\\)"}</SafeMJ> be a non-zero polynomial of degree <SafeMJ after=",">{"\\(k\\)"}</SafeMJ>{" "}
      then <SafeMJ>{"\\(m(x)\\)"}</SafeMJ> has at most <SafeMJ>{"\\(k\\)"}</SafeMJ> distinct roots in <SafeMJ after=".">{"\\(K\\)"}</SafeMJ>
    </p>

    <Proof>
      <p className="mb-3">
        <em>Proof.</em> Let's proceed by induction on <SafeMJ after=".">{"\\(k\\)"}</SafeMJ>
      </p>
      <p className="mb-3">
        If <SafeMJ after=",">{"\\(k=0\\)"}</SafeMJ> <SafeMJ>{"\\(m(x)\\)"}</SafeMJ> is a non-zero constant and has no roots, so the statement holds trivially.
      </p>
      <p className="mb-3">
        If <SafeMJ>{"\\(k\\ge 1\\)"}</SafeMJ> and <SafeMJ>{"\\(m(x)\\)"}</SafeMJ> simply has no roots, the lemma again holds trivially.
        If, on the other hand, <SafeMJ>{"\\(\\alpha\\in K\\)"}</SafeMJ> is a root of <SafeMJ after=",">{"\\(m(x)\\)"}</SafeMJ> then
        we apply Euclidean division in <SafeMJ>{"\\(K[x]\\)"}</SafeMJ> by <SafeMJ after=",">{"\\(x-\\alpha\\)"}</SafeMJ> obtaining:
        <SafeMJ scrollable>{"\\[m(x)=q(x)(x-\\alpha)+r,\\quad \\textnormal{with } r\\in K.\\]"}</SafeMJ>
        Evaluating at <SafeMJ>{"\\(\\alpha\\)"}</SafeMJ> gives <SafeMJ after=",">{"\\(r=0\\)"}</SafeMJ> so:
        <SafeMJ scrollable>{"\\[m(x)=q(x)(x-\\alpha),\\quad \\textnormal{with } \\deg(q)<n.\\]"}</SafeMJ>
        Since, by the induction hypothesis, <SafeMJ>{"\\(q(x)\\)"}</SafeMJ> has at most <SafeMJ>{"\\(k-1\\)"}</SafeMJ> distinct roots in <SafeMJ after=",">{"\\(K\\)"}</SafeMJ>{" "}
        <SafeMJ>{"\\(m(x)\\)"}</SafeMJ> will have at most <SafeMJ>{"\\(k\\)"}</SafeMJ> distinct roots (the <SafeMJ>{"\\(k-1\\)"}</SafeMJ> of <SafeMJ>{"\\(q(x)\\)"}</SafeMJ> and <SafeMJ after=").">{"\\(\\alpha\\)"}</SafeMJ>
        <span className="qed"><SafeMJ>{"\\(\\square\\)"}</SafeMJ></span>
      </p>
    </Proof>
  
    <p className="mb-3">
      Note that from this silly little result, the following corollary follows automatically:
    </p>
  
    <p className="mb-3">
      <b>Corollary 2.3.</b> If <SafeMJ>{"\\(m(x)\\in K[x]_{<k}\\)"}</SafeMJ> has <SafeMJ>{"\\(k\\)"}</SafeMJ> distinct roots in <SafeMJ after=",">{"\\(K\\)"}</SafeMJ>{" "}
      then <SafeMJ after=".">{"\\(m(x)\\equiv 0\\)"}</SafeMJ>
    </p>
    
    <p className="mb-3">
      And with this we can now go on to prove the linearity and dimension of Reed-Solomon:
    </p>
    
    <Proof>
    <p className="mb-3">
      <em>Proof of <SafeMJ after=".">{"\\(\\textit{(2.1)}\\)"}</SafeMJ></em> Since <SafeMJ>{"\\(\\mathbb{F}_q[x]_{<k}\\)"}</SafeMJ> is an <SafeMJ after="vector">{"\\(\\mathbb{F}_q\\text{-}\\)"}</SafeMJ> space of dimension <SafeMJ>{"\\(k\\)"}</SafeMJ> (note
      that <SafeMJ>{"\\(\\{1,x,x^2,\\dots,x^{k-1}\\}\\)"}</SafeMJ> is a basis), we have the following linear map:
      <SafeMJ scrollable>{"\\[\\mathrm{ev}:\\;\\mathbb{F}_q[x]_{<k}\\longrightarrow\\mathbb{F}_q^n,\\quad m(x)\\longmapsto(m(\\alpha_1),\\dots,m(\\alpha_n)).\\]"}</SafeMJ>
      This automatically makes <SafeMJ>{"\\(\\mathrm{RS}(n,k)=\\mathrm{Im}(\\mathrm{ev})\\)"}</SafeMJ> an <SafeMJ after="vector">{"\\(\\mathbb{F}_q\\text{-}\\)"}</SafeMJ> subspace of <SafeMJ after=".">{"\\(\\mathbb{F}_q^n\\)"}</SafeMJ>{" "}
      
      Moreover, the kernel of <SafeMJ>{"\\(\\mathrm{ev}\\)"}</SafeMJ> is made up of the polynomials of degree less than <SafeMJ>{"\\(k\\)"}</SafeMJ> with <SafeMJ>{"\\(\\alpha_1,\\dots,\\alpha_n\\)"}</SafeMJ> as distinct roots.
      Since <SafeMJ after=",">{"\\(n\\ge k\\)"}</SafeMJ> by the previous corollary the kernel is trivial and <SafeMJ>{"\\(\\mathrm{ev}\\)"}</SafeMJ> is injective. Thus, using the famous rank-nullity theorem, we have
      que <SafeMJ after=".">{"\\(\\dim{\\mathrm{RS}(n,k)}=\\dim{\\mathbb{F}_q[x]_{<k}}=k\\)"}</SafeMJ><span className="qed"><SafeMJ>{"\\(\\square\\)"}</SafeMJ></span>
    </p>
    </Proof>

    <p className="mb-3">
      An important detail: when we saw that the evaluation map was injective, we were actually saying something deeper. If <SafeMJ>{"\\(\\mathrm{ev}\\)"}</SafeMJ> weren't injective,
      that would mean there would be two different messages encoded into the same word, which would be a nuisance for the receiver. How would it know which of the two messages the received word corresponds to?
      In other words, the injectivity of <SafeMJ>{"\\(\\mathrm{ev}\\)"}</SafeMJ> gives us peace of mind that, as long as no errors occur during transmission,
      we'll be able to decode unambiguously. By the way, would the same have happened if we hadn't required <SafeMJ after="?">{"\\(k\\le n\\)"}</SafeMJ> I'll leave that one for you to think about...
    </p>

    <p className="mb-3">
      Now everything's in order. Let's carry on, then, with more properties of this beast we've built. For example, making use of the evaluation map we defined in the previous proof,
      we can rewrite the Reed-Solomon encoding process as the multiplication of a vector by a matrix.
    </p>

    <p className="mb-3">
      Indeed, let <SafeMJ>{"\\(V\\in\\mathbb{F}_q^{n\\times k}\\)"}</SafeMJ> be the matrix of <SafeMJ>{"\\(\\mathrm{ev}\\)"}</SafeMJ> in the basis <SafeMJ>{"\\(\\{1,x,x^2,\\dots,x^{k-1}\\}\\)"}</SafeMJ> of <SafeMJ after=",">{"\\(\\mathbb{F}_q[x]_{<k}\\)"}</SafeMJ> we know
      from linear algebra that to find its columns it's enough to evaluate the map at the basis elements. That is:
      <SafeMJ scrollable>{"\\[V_{ij}=\\mathrm{ev}(x^{j-1})_i=\\alpha_i^{j-1},\\]"}</SafeMJ>
      or, in matrix form:
      <SafeMJ scrollable>{"\\[V=\\begin{pmatrix}1&\\alpha_1&\\alpha_1^2&\\cdots&\\alpha_1^{k-1}\\\\ 1&\\alpha_2&\\alpha_2^2&\\cdots&\\alpha_2^{k-1}\\\\ \\vdots&\\vdots&\\vdots&\\ddots&\\vdots\\\\ 1&\\alpha_n&\\alpha_n^2&\\cdots&\\alpha_n^{k-1}\\end{pmatrix}\\in\\mathbb{F}_q^{n\\times k}.\\]"}</SafeMJ>
      This matrix is known as the <em>Vandermonde matrix</em> (the rectangular version). A somewhat spooky name, but hey, that was the man's name. You probably remember it from school, since it came up when computing determinants.
    </p>

    <p className="mb-3">
      We know, from the previous proposition, that this matrix has rank <SafeMJ>{"\\(k\\)"}</SafeMJ> and, moreover, we can use it
      to simplify the Reed-Solomon encoding process to:
      <SafeMJ scrollable>{"\\[c=Vm.\\tag{2.4}\\]"}</SafeMJ>
      Quite often, the above expression is written by rows, that is, like this: <SafeMJ scrollable>{"\\[c^\\top=m^\\top G,\\tag{2.5}\\]"}</SafeMJ>
      where <SafeMJ>{"\\(G=V^\\top\\)"}</SafeMJ> is the so-called <b>generator matrix</b> of the code. It gets this name because, by the properties
      of linear maps, its rows (the columns of <SafeMJ after=")">{"\\(V\\)"}</SafeMJ> form a basis of <SafeMJ after=".">{"\\(\\mathrm{RS}(n,k)\\)"}</SafeMJ>
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">3. Decoding without errors</h2>
    
    <p className="mb-3">
      Now that we know how to encode messages with Reed-Solomon, the natural next step is to learn how to decode them.
      That is, so far we know the easy role of the sender, who simply multiplies by a matrix and washes their hands of it, but we still have to
      break down the receiver's. The latter's, unfortunately, isn't usually so easy, since anomalies like
      the following can occur during transmission:
      <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1"><b>Erasure:</b> a component of the transmitted word gets lost along the way.</li>
        <SafeMJ scrollable>{"\\[c=(3,0,3,2,2)\\longrightarrow r=(3,\\square,3,2,2)\\]"}</SafeMJ>
        <li className="mb-1"><b>Error:</b> a component of the transmitted word gets altered along the way.</li>
        <SafeMJ scrollable>{"\\[c=(3,0,3,2,2)\\longrightarrow r=(3,0,2,2,2)\\]"}</SafeMJ>
      </ul>
      Note, and this is important, that in the first case the receiver knows the position that was affected during transmission, which, a priori,
      is a mystery in the case of an error.
    </p>

    <p className="mb-3">
      Right, so thinking now about the receiver's modus operandi, let's start with the easy case. Suppose no errors or erasures occurred during the transmission of the word, that is, everything went perfectly.
      Then, the first thing that comes to mind is simply to reverse expression <SafeMJ after=",">{"\\((2.4)\\)"}</SafeMJ> the same one the sender used to encode the message. Thinking
      of <SafeMJ>{"\\(m\\)"}</SafeMJ> as a vector of unknowns and of <SafeMJ>{"\\(c\\)"}</SafeMJ> as a vector of constant terms, we realise we're simply facing
      a linear system of <SafeMJ>{"\\(n\\)"}</SafeMJ> equations and <SafeMJ>{"\\(k\\)"}</SafeMJ> unknowns, which we handle with complete ease. What's more, this works, if you think about it, for any linear code we
      like, not just Reed-Solomon. All it takes is finding a generator matrix of the code, setting up system <SafeMJ>{"\\((2.5)\\)"}</SafeMJ> and solving...
    </p>
    <p className="mb-3">
      Okay, easy, but admittedly this was the good case... What happens if, for instance, the word suffers erasures? Don't panic.
      It can easily be proved by induction that the Vandermonde determinant, that is, that of any submatrix <SafeMJ>{"\\(V[i_1,\\ldots,i_k;:]\\)"}</SafeMJ> of <SafeMJ>{"\\(V\\)"}</SafeMJ> of
      size <SafeMJ after=",">{"\\(k\\times k\\)"}</SafeMJ> is:
      <SafeMJ scrollable>{"\\[\\prod_{1\\le p<q\\le k}(\\alpha_{i_q}-\\alpha_{i_p}),\\]"}</SafeMJ>
      which is always non-zero since all the <SafeMJ after="'s">{"\\(\\alpha_i\\)"}</SafeMJ> are pairwise distinct. This implies that,
      whichever <SafeMJ>{"\\(k\\)"}</SafeMJ> equations of <SafeMJ>{"\\((2.4)\\)"}</SafeMJ> we keep, the system will still have the same unique solution.
      Note that what this really means is that with Reed-Solomon <b>we can afford to suffer up to <SafeMJ>{"\\(\\boldsymbol{n-k}\\)"}</SafeMJ> erasures in a word</b>, since we could
      discard the incomplete equations without any problem and solve the system with the remaining ones. Wonderful! And obviously, the larger the difference between <SafeMJ>{"\\(n\\)"}</SafeMJ> and <SafeMJ after=",">{"\\(k\\)"}</SafeMJ>{" "}
      the more resilient the code will be (although also heavier, of course).
    </p>
    <p className="mb-3"> 
      As you'll have noticed, this no longer applies to any
      linear code, as it did before, since it isn't true that every generator matrix of rank <SafeMJ>{"\\(k\\)"}</SafeMJ> has all its minors of order <SafeMJ>{"\\(k\\)"}</SafeMJ> non-zero;
      all we know is that at least one of them is non-zero. So depending on which components get erased, the system could have several solutions...
      The other thing, the stronger one, is a peculiarity specific to Reed-Solomon.
    </p>

    <p className="mb-3">
      Cool, so does it all boil down to solving a linear system in the end? Is that it? Well, not quite... You know that methods like Gaussian elimination, the classic of building
      a staircase of zeros, have, except in special cases, a computational cost of <SafeMJ after=".">{"\\(O(k^3)\\)"}</SafeMJ> Bearing in mind
      that there are situations, like a DVD, for example, where the amount of data is quite considerable and where we want to get close to
      real time, it's almost mandatory to look for something more efficient.
    </p>

    <p className="mb-3">
      Luckily, in the case of Reed-Solomon, there's a very simple polynomial-based mathematical mechanism that manages to reduce the previous computational cost.
      The key lies in the following proposition:
    </p>

    <p className="mb-3">
      <b>Proposition 3.1.</b> Let <SafeMJ>{"\\(K\\)"}</SafeMJ> be a field and let <SafeMJ>{"\\((\\alpha_1,y_1),\\dots,(\\alpha_k,y_k)\\in K\\times K\\)"}</SafeMJ> be points with <SafeMJ after=":">{"\\(\\alpha_i\\neq\\alpha_j,\\,\\forall i\\neq j\\)"}</SafeMJ>
      <SafeMJ scrollable>{"\\[L(x)=\\sum_{i=1}^{k}y_i\\prod_{j\\neq i}^{k}\\frac{x-\\alpha_j}{\\alpha_i-\\alpha_j}\\]"}</SafeMJ>
      is the unique polynomial of <SafeMJ>{"\\(K[x]_{<k}\\)"}</SafeMJ> satisfying <SafeMJ after=".">{"\\(L(\\alpha_i)=y_i,\\,\\forall i=1,\\ldots,k\\)"}</SafeMJ>
    </p>
    <p className="mb-3">
      <SafeMJ>{"\\(L(x)\\)"}</SafeMJ> goes by the special name of <b>Lagrange interpolation polynomial</b>. Interpolating, in this case,
      means "passing through the points". Let's first look at its proof, which is basically checking that everything adds up, and then at how we can
      use it in our particular case to decode faster.
    </p>

    <Proof>
    <p className="mb-3">
      <em>Proof.</em> For each <SafeMJ after=",">{"\\(i\\in\\{1,\\ldots,k\\}\\)"}</SafeMJ> we define the following auxiliary polynomial of <SafeMJ after=":">{"\\(K[x]\\)"}</SafeMJ>
      <SafeMJ scrollable>{"\\[\\ell_i(x):=\\prod_{j\\neq i}^{k}\\frac{x-\\alpha_j}{\\alpha_i-\\alpha_j}=\\frac{x-\\alpha_1}{\\alpha_i-\\alpha_1}\\cdots\\frac{x-\\alpha_{i-1}}{\\alpha_i-\\alpha_{i-1}}\\cdot\\frac{x-\\alpha_{i+1}}{\\alpha_i-\\alpha_{i+1}}\\cdots\\frac{x-\\alpha_k}{\\alpha_i-\\alpha_k},\\]"}</SafeMJ>
      which has degree <SafeMJ>{"\\(k-1\\)"}</SafeMJ> and satisfies, for all <SafeMJ after=":">{"\\(s\\in\\{1,\\ldots,k\\}\\)"}</SafeMJ>
      <SafeMJ scrollable>{"\\[\\ell_i(\\alpha_s)=\\delta_{is}=\\begin{cases}1, & i=s,\\\\ 0, & i\\neq s.\\end{cases}\\]"}</SafeMJ>
      Then, <SafeMJ>{"\\(L(x)=\\sum_{i=1}^n y_i \\ell_i(x)\\)"}</SafeMJ> has degree less than <SafeMJ>{"\\(k\\)"}</SafeMJ> and, evaluating it at the <SafeMJ after="'s,">{"\\(\\alpha_s\\)"}</SafeMJ> we see that it interpolates the given points.
    </p>
    <p className="mb-3">
      As for uniqueness, let <SafeMJ>{"\\(L_1(x),L_2(x)\\in K[x]_{<k}\\)"}</SafeMJ> both interpolate the given points, then <SafeMJ>{"\\(D(x)=L_1(x)-L_2(x)\\)"}</SafeMJ> has degree less than <SafeMJ>{"\\(k\\)"}</SafeMJ> and
      vanishes at the <SafeMJ>{"\\(k\\)"}</SafeMJ> distinct points <SafeMJ after=".">{"\\(\\alpha_1,\\ldots,\\alpha_k\\)"}</SafeMJ> So, by corollary <SafeMJ after=",">{"\\((2.3)\\)"}</SafeMJ> we necessarily have{" "}
      <SafeMJ after=",">{"\\(D(x)\\equiv 0\\)"}</SafeMJ> that is, <SafeMJ after=".">{"\\(L_1(x)\\equiv L_2(x)\\)"}</SafeMJ>
      <span className="qed"><SafeMJ>{"\\(\\square\\)"}</SafeMJ></span>
    </p>
    </Proof>

    <p className="mb-3">
      The previous proposition, written like this, doesn't say much. However, reading it the other way round, we get the winning idea:
      a polynomial of degree less than <SafeMJ>{"\\(k\\)"}</SafeMJ> is completely determined by its value at any <SafeMJ>{"\\(k\\)"}</SafeMJ>{" "}
      points. Therefore, if we evaluate it at <SafeMJ>{"\\(n\\ge k\\)"}</SafeMJ> points, the <SafeMJ>{"\\(n-k\\)"}</SafeMJ> extra
      values are redundant and can be lost during transmission. It's actually the same conclusion we reached before, just without going
      through matrices, Vandermonde determinants and so on.
    </p>

    <p className="mb-3">
      The powerful bit, however, doesn't come from the conclusion, but from the process. It turns out that computing the Lagrange polynomial, and therefore decoding the word
      (remember that its coefficients will be those of the original message), is more efficient than setting up the linear system we saw before. Note that to find
      the coefficients of the interpolation polynomial, we need to carry out <SafeMJ>{"\\(k\\)"}</SafeMJ> sums of <SafeMJ>{"\\(k-1\\)"}</SafeMJ> products, so the total
      cost comes down to <SafeMJ after=".">{"\\(O(k^2)\\)"}</SafeMJ> Much better... There are even faster methods based on the discrete Fourier transform,
      but hey, I think that's getting slightly out of hand.
    </p>

    <p className="mb-3">
      So, to wrap up this part, let's look at a simple example. Let's take the case of <SafeMJ>{"\\(\\textrm{`}ouo\\textrm{'}\\)"}</SafeMJ> from the second section
      and assume the erasure from before:
      <SafeMJ scrollable>{"\\[m=(3,4,3)\\longrightarrow c=(3,0,3,2,2)\\longrightarrow r=(3,\\square,3,2,2).\\]"}</SafeMJ>
      Remember that we had <SafeMJ>{"\\(q=n=5\\)"}</SafeMJ> and <SafeMJ after=",">{"\\(k=3\\)"}</SafeMJ> so we could actually afford up to <SafeMJ>{"\\(n-k=2\\)"}</SafeMJ> erasures.
      Basically, we even have a coordinate to spare in this case. Then, taking, for example, the last 3 coordinates of the received word, the interpolation polynomial becomes:
      <SafeMJ scrollable>{`
        \\[
        \\begin{aligned}
        m(x) &= 3\\cdot\\frac{x-3}{2-3}\\cdot\\frac{x-4}{2-4} + 2\\cdot\\frac{x-2}{3-2}\\cdot\\frac{x-4}{3-4} + 2\\cdot\\frac{x-2}{4-2}\\cdot\\frac{x-3}{4-3} \\\\[5pt]
        &= 4\\cdot(x-3)\\cdot(x-4) + 3\\cdot(x-2)\\cdot(x-4) + (x-2)\\cdot(x-3) \\\\[5pt]
        &= (4x^2+2x+3) + (3x^2+2x+4) + (x^2+1) \\\\[5pt]
        &= 3x^2+4x+3,
        \\end{aligned}
        \\]
      `}</SafeMJ>
      and we've recovered the original message, just like that.

    </p>

    <p className="mb-3">
      Ah! One last thing before moving on. I didn't mention it earlier because it seemed a given, but I'll say it just in case.
      Obviously, all the "configuration parameters" of the code used (for example, in Reed-Solomon: <SafeMJ after=",">{"\\(n\\)"}</SafeMJ> <SafeMJ after=",">{"\\(q\\)"}</SafeMJ> <SafeMJ after=",">{"\\(k\\)"}</SafeMJ>{" "}
      the <SafeMJ after="'s,">{"\\(\\alpha_i\\)"}</SafeMJ> ...) are openly known by the sender and the receiver. So don't confuse 'encoding' with 'encrypting', eh, there's no cryptography or secrets here.
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">4. Minimum distance and error-correcting capability</h2>

    <p className="mb-3">
      We've learnt to decode, in several ways no less, codewords received perfectly or with a reasonable number of erasures.
      Let's now raise the bar a bit and start thinking about what happens when they include alterations in their components.
      For that, we first need to introduce some key theoretical concepts in error detection and correction.
    </p>
    <p className="mb-3">
      First, given two words <SafeMJ after=",">{"\\(u,v\\in\\mathbb{F}_q^n\\)"}</SafeMJ> we define their <b>Hamming distance</b> as the number of coordinates in which they differ. That is:
      <SafeMJ scrollable>{"\\[d_H(u,v):=\\#\\{i:u_i\\neq v_i\\}.\\]"}</SafeMJ>
    </p>
    
    <p className="mb-3">
      In the case of a code <SafeMJ after=",">{"\\(\\mathcal{C}\\subset\\mathbb{F}_q^n\\)"}</SafeMJ> its <b>minimum Hamming distance</b> is defined, naturally, as the minimum Hamming distance between two distinct words of the code:
      <SafeMJ scrollable>{"\\[d(\\mathcal{C}):=\\min\\{d_H(c,c'):c,c'\\in\\mathcal{C},\\;c\\neq c'\\}.\\]"}</SafeMJ>
    </p>

    <p className="mb-3">
      Note that in the previous definitions we've used the word 'distance' very loosely. The proper thing to do is to check that the properties of this mathematical object really hold. Recall:
      non-negativity, identity, symmetry and the triangle inequality, which I'll leave as an exercise so as not to drag the article out. You'll see it's not hard, especially for the first distance.
    </p>

    <p className="mb-3">
      Right, but why are these distances important? Well, look. Imagine we're communicating with a code <SafeMJ>{"\\(\\mathcal{C}\\)"}</SafeMJ> with minimum
      distance <SafeMJ>{"\\(d\\)"}</SafeMJ> and that during the transmission of one of its words, <SafeMJ after=",">{"\\(c\\)"}</SafeMJ> <SafeMJ>{"\\(e\\ge 1\\)"}</SafeMJ> of its components get altered.
      Then, as long as <SafeMJ after=",">{"\\(e<d\\)"}</SafeMJ> it will be impossible for the resulting word <SafeMJ>{"\\(r\\)"}</SafeMJ> to coincide with another word of the code,
      since in that case we would have <SafeMJ after=",">{"\\(d_H(c,r)=e<d\\)"}</SafeMJ> contradicting the minimality of <SafeMJ after=".">{"\\(d\\)"}</SafeMJ>{" "}
      And so, as receivers, we'll be able to state that the word has suffered transmission errors.
    </p>

    <p className="mb-3">
      But there's more. Suppose something a tad more restrictive, that <SafeMJ after=",">{"\\(e\\le t=\\lfloor(d-1)/2\\rfloor\\)"}</SafeMJ> and consider any other word <SafeMJ>{"\\(c'\\neq c\\)"}</SafeMJ> of the code. Then, it can't
      be that <SafeMJ after=",">{"\\(d_H(c',r) \\le d_H(c,r)\\)"}</SafeMJ> since by the triangle inequality we would have:
      <SafeMJ scrollable>{"\\[d\\le d_H(c,c')\\le d_H(c,r)+d_H(r,c')\\le 2 d_H(c,r)=2e\\le d-1\\;!!!\\]"}</SafeMJ>
      That is, necessarily <SafeMJ after=",">{"\\(d_H(c',r) > d_H(c,r)\\)"}</SafeMJ> or in other words: the smallest distance to the received word is given, unambiguously, by the original word! That's great,
      because it means we could recover from the transmission error by looking for the valid word (that is, one in the code) closest to the received one:
      <SafeMJ scrollable>{"\\[c=\\underset{c'\\in\\mathcal{C}}{\\arg\\min}\\; d_H(r,c').\\tag{4.1}\\]"}</SafeMJ>
    </p>
    
    <p className="mb-3">
      To sum up, we've just seen that for any code with minimum distance <SafeMJ after=",">{"\\(d\\)"}</SafeMJ> we can:
    <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
      <li className="mb-1"><b>Detect words with up to <SafeMJ>{"\\(\\boldsymbol{d-1}\\)"}</SafeMJ> errors</b>.</li>
      <li className="mb-1"><b>Correct words with up to <SafeMJ>{"\\(\\boldsymbol{t=\\lfloor(d-1)/2\\rfloor}\\)"}</SafeMJ> errors</b>.</li>
    </ul>
    Outside the first range, unfortunately, we might not know whether the received word is the original one or whether, on the contrary,
    it mutated so much along the way that it became another valid codeword. A pain, I know. And the same goes for the second one.
    If <SafeMJ after=",">{"\\(t<e<d\\)"}</SafeMJ> then we'll know the word is corrupted, but the closest codeword might not be the original.
    </p>

    <p className="mb-3">
    Therefore, we'll always try to use codes with the largest possible minimum distance, since the wider our range for detecting and correcting errors will then be.
    Mind you, only up to an unbeatable ceiling, as the following proposition shows:
    </p>

    <p className="mb-3">
      <b>Proposition 4.2.</b> Every code <SafeMJ>{"\\(\\mathcal{C}\\subset\\mathbb{F}_q^n\\)"}</SafeMJ> with minimum distance <SafeMJ>{"\\(d\\)"}</SafeMJ> satisfies:
      <SafeMJ scrollable>{"\\[|\\mathcal{C}|\\le q^{n-d+1}.\\]"}</SafeMJ>
      This upper bound is known as the <b>Singleton bound</b>, and a code that attains it is called <b>MDS</b> (Maximum Distance Separable).
    </p>

    <Proof>
    <p className="mb-3">
      <em>Proof.</em> Consider the projection:
      <SafeMJ scrollable>{"\\[\\pi:\\;\\mathcal{C}\\longrightarrow\\mathbb{F}_q^{n-d+1},\\quad (c_1,\\dots,c_n)\\longmapsto(c_1,\\dots,c_{n-d+1}),\\]"}</SafeMJ>
      which is injective, since if we had two words <SafeMJ>{"\\(c\\neq c'\\)"}</SafeMJ> with <SafeMJ after=",">{"\\(\\pi(c)=\\pi(c')\\)"}</SafeMJ> they would differ at most in the last <SafeMJ>{"\\(d-1\\)"}</SafeMJ> coordinates,
      contradicting <SafeMJ after=".">{"\\(d_H(c,c')\\ge d\\)"}</SafeMJ> Therefore, comparing cardinalities, we get <SafeMJ after=".">{"\\(|\\mathcal{C}|\\le |\\mathbb{F}_q^{n-d+1}|= q^{n-d+1}\\)"}</SafeMJ><span className="qed"><SafeMJ>{"\\(\\square\\)"}</SafeMJ></span>
    </p>
    </Proof>

    <p className="mb-3">
      Note, moreover, that in the case of a linear code of dimension <SafeMJ after=",">{"\\(k\\)"}</SafeMJ> the previous expression simplifies very nicely,
      since <SafeMJ>{"\\(q^k=|\\mathcal{C}|\\le q^{n-d+1}\\)"}</SafeMJ> holds if and only if:
      <SafeMJ scrollable>{"\\[d\\le n-k+1,\\]"}</SafeMJ>
      and then, in the case of equality, words with up to <SafeMJ>{"\\(t=\\bigl\\lfloor\\tfrac{n-k}{2}\\bigr\\rfloor\\)"}</SafeMJ> errors can be corrected.
    </p>

    <p className="mb-3">
      As for the Reed-Solomon code, which is the case we're really interested in, there's good news. The previous inequality is actually an equality, that is,{" "}
      <b>Reed-Solomon is MDS</b>. To see this, it's enough to take any pair of distinct words <SafeMJ after=".">{"\\(c,c'\\in\\mathrm{RS}(n,k)\\)"}</SafeMJ> Then, letting <SafeMJ>{"\\(m_1(x),m_2(x)\\in\\mathbb{F}_q[x]_{<k}\\)"}</SafeMJ> be their
      respective associated polynomials, we consider their difference <SafeMJ after=".">{"\\(r(x)=m_1(x)-m_2(x)\\neq 0\\)"}</SafeMJ>{" "}
      By lemma <SafeMJ after=",">{"\\((2.2)\\)"}</SafeMJ> <SafeMJ>{"\\(r(x)\\)"}</SafeMJ> has at most <SafeMJ>{"\\(k-1\\)"}</SafeMJ> distinct roots
      in <SafeMJ after=",">{"\\(\\mathbb{F}_q\\)"}</SafeMJ> so among <SafeMJ>{"\\(r(\\alpha_1),\\dots,r(\\alpha_n)\\)"}</SafeMJ> there are at most <SafeMJ>{"\\(k-1\\)"}</SafeMJ> zeros. This implies:
      <SafeMJ scrollable>{"\\[d_H(c,c')=\\#\\{i: m_1(\\alpha_i)\\neq m_2(\\alpha_i)\\}\\ge n-(k-1)=n-k+1,\\]"}</SafeMJ>
      and therefore, taking the minimum:
      <SafeMJ scrollable>{"\\[d=\\min_{c\\neq c'}{d_H(c,c')} \\ge n-k+1.\\]"}</SafeMJ>
    </p>

    <p className="mb-3">
      And finally, to cover all cases: what happens if the received word contains both errors and erasures? Well, basically
      each erasure "is worth two errors", so to speak, so the condition that must hold in order to recover the original word is:
      <SafeMJ scrollable>{"\\[2e+b<d,\\]"}</SafeMJ>
      where <SafeMJ>{"\\(b\\)"}</SafeMJ> is the number of erasures. I'll leave the proof as an exercise,
      which is simply a matter of generalising the triangle inequality we saw before, bearing in mind that erased positions can't be compared.
      We therefore conclude that in an MDS linear code, as is the case with Reed-Solomon, any combination of errors and erasures can be corrected
      as long as it satisfies:
      <SafeMJ scrollable>{"\\[2e+b\\le n-k.\\tag{4.3}\\]"}</SafeMJ>
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">5. Decoding with errors</h2>

    <p className="mb-3">
      Hang on, what's this section for? Didn't we say that with the search in <SafeMJ>{"\\((4.1)\\)"}</SafeMJ> we can recover from
      errors? Ah, right, wait, that brute force is all very well in theory, but in practice it's intractable. Bear in mind that computing each
      distance takes <SafeMJ>{"\\(n\\)"}</SafeMJ> component comparisons, and since there are <SafeMJ>{"\\(q^k\\)"}</SafeMJ> words in the code,
      the total cost goes up to <SafeMJ after=".">{"\\(O(nq^k)\\)"}</SafeMJ> And that's exponential in <SafeMJ after="!">{"\\(k\\)"}</SafeMJ>
    </p>

    <p className="mb-3">
      We therefore need to find an algorithm that is actually affordable for this task. To do so, let's go back to the situation from before and suppose we receive a word <SafeMJ>{"\\(r\\in\\mathbb{F}_q^n\\)"}</SafeMJ> with
      a number of errors <SafeMJ>{"\\(e\\)"}</SafeMJ> no greater than the error-correcting capability of Reed-Solomon, that is, satisfying <SafeMJ after=".">{"\\(e\\le t=\\lfloor(n-k)/2\\rfloor\\)"}</SafeMJ>
    </p>

    <p className="mb-3">
      Then, letting <SafeMJ>{"\\(m(x)\\in\\mathbb{F}_q[x]_{<k}\\)"}</SafeMJ> be the original message polynomial and <SafeMJ>{"\\(I:=\\{i:m(\\alpha_i)\\neq r_i\\}\\)"}</SafeMJ> the set of erroneous positions, we define the <b>error locator polynomial</b> as:
      <SafeMJ scrollable>{"\\[E^{\\star}(x):=\\prod_{i\\in I}(x-\\alpha_i),\\]"}</SafeMJ>
      which is monic and has degree <SafeMJ after=".">{"\\(e\\)"}</SafeMJ> Its usefulness is that it vanishes precisely at the <SafeMJ after="'s">{"\\(\\alpha_i\\)"}</SafeMJ> corresponding
      to coordinates with errors, which means that, for all <SafeMJ after=",">{"\\(i\\in\\{1,\\dots,n\\}\\)"}</SafeMJ> the following key identity holds:
      <SafeMJ scrollable>{"\\[m(\\alpha_i)\\,E^{\\star}(\\alpha_i)=r_i\\,E^{\\star}(\\alpha_i).\\tag{5.1}\\]"}</SafeMJ>
      Indeed, if <SafeMJ after=",">{"\\(i\\in I\\)"}</SafeMJ> then both sides are <SafeMJ after=".">{"\\(0\\)"}</SafeMJ> In this case, <SafeMJ>{"\\(E^\\star(x)\\)"}</SafeMJ> "switches off", so to speak,
      the discrepancy between <SafeMJ>{"\\(m(\\alpha_i)\\)"}</SafeMJ> and <SafeMJ after=".">{"\\(r_i\\)"}</SafeMJ> If, on the other hand, <SafeMJ after=",">{"\\(i\\not\\in I\\)"}</SafeMJ>{" "}
      then there's no error in the <SafeMJ after="th">{"\\(i\\text{-}\\)"}</SafeMJ> position and <SafeMJ>{"\\(m(\\alpha_i)\\)"}</SafeMJ> coincides with <SafeMJ after=".">{"\\(r_i\\)"}</SafeMJ>
    </p>

    <p className="mb-3">
      Now, note that the left-hand side of the previous equality still involves <SafeMJ after=",">{"\\(m(x)\\)"}</SafeMJ> which is the unknown we want to solve for,
      so it's not crazy at all to absorb it into the following polynomial: <SafeMJ after=".">{"\\(Q^{\\star}(x):=m(x)\\,E^{\\star}(x)\\)"}</SafeMJ>{" "}
      So, since <SafeMJ after=",">{"\\(\\deg(m)<k\\)"}</SafeMJ> then <SafeMJ after=",">{"\\(\\deg(Q^{\\star})<k+e\\)"}</SafeMJ> but
      above all, and more importantly, the previous identity then becomes:
      <SafeMJ scrollable>{"\\[Q^{\\star}(\\alpha_i)=r_i\\,E^{\\star}(\\alpha_i),\\quad\\forall{i=1,\\ldots,n}.\\tag{5.2}\\]"}</SafeMJ>
      And this... is much more powerful than it looks, since by writing out the coefficients of the polynomials:
      <SafeMJ scrollable>{"\\[Q^{\\star}(x)=\\sum_{j=0}^{k+e-1}q_jx^j,\\quad E^{\\star}(x)=x^{e}+\\sum_{j=0}^{e-1}e_jx^j,\\]"}</SafeMJ>
      expression <SafeMJ>{"\\((5.2)\\)"}</SafeMJ> becomes:
      <SafeMJ scrollable>{"\\[\\sum_{j=0}^{k+e-1}q_j\\alpha_i^j-r_i\\sum_{j=0}^{e-1}e_j\\alpha_i^j=r_i\\alpha_i^e,\\quad\\forall{i=1,\\ldots,n},\\tag{5.3}\\]"}</SafeMJ>
      which is nothing more than a linear system of <SafeMJ>{"\\(n\\)"}</SafeMJ> equations with <SafeMJ>{"\\(2e+k\\)"}</SafeMJ> unknowns (<SafeMJ>{"\\(k+e\\)"}</SafeMJ> <SafeMJ after="'s">{"\\(q_j\\)"}</SafeMJ>{" "}
      and <SafeMJ>{"\\(e\\)"}</SafeMJ> <SafeMJ after="'s),">{"\\(e_j\\)"}</SafeMJ> which we know has the solution <SafeMJ>{"\\((E^\\star(x), Q^\\star(x))\\)"}</SafeMJ> by construction.
    </p>

    <p className="mb-3">
      Ah, great! Then it's clear: we solve the system, find <SafeMJ>{"\\(Q^{\\star}(x)\\)"}</SafeMJ> and <SafeMJ>{"\\(E^{\\star}(x)\\)"}</SafeMJ> and recover <SafeMJ>{"\\(m(x)\\)"}</SafeMJ> simply by
      dividing the first polynomial by the second.
      How easy! Well, not so fast, cowboy... The idea is more or less that, but there are two details we haven't talked about and without
      which the previous algorithm could crash:
      <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1"> The first one. Note that you know <SafeMJ>{"\\((5.3)\\)"}</SafeMJ> has a solution, but you don't know whether it has more. You could set up
        the system and end up with infinitely many solutions to choose from.</li>
        <li className="mb-1">And second, and even worse, from a practical point of view, you can't even set up the previous system! Why, you ask?
        Because the receiver has no idea of the number <SafeMJ after=".">{"\\(e\\)"}</SafeMJ> In a real transmission process, it only receives the word <SafeMJ after=",">{"\\(r\\)"}</SafeMJ>{" "}
        which has suffered who knows how many errors along the way. Ugh, that's true...
        </li>
      </ul>
      Well, relax. Don't sigh, because luckily the following proposition is going to sort our lives out:
    </p>

    <p className="mb-3">
      <b>Proposition 5.4.</b> For any pair of polynomials <SafeMJ>{"\\(Q(x),E(x)\\in\\mathbb{F}_q[x]\\)"}</SafeMJ> satisfying:
      <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1"><SafeMJ>{"\\(E(x)\\)"}</SafeMJ> is monic with <SafeMJ after=",">{"\\(\\deg(E)\\le t\\)"}</SafeMJ></li>
        <li className="mb-1"><SafeMJ after=",">{"\\(\\deg(Q)< t+k\\)"}</SafeMJ></li>
        <li className="mb-1"><SafeMJ after=",">{"\\(Q(\\alpha_i)=r_i\\,E(\\alpha_i),\\,\\forall i=1,\\dots,n\\)"}</SafeMJ></li>
      </ul>
      it holds that: <SafeMJ scrollable>{"\\[Q(x)=m(x)\\,E(x).\\]"}</SafeMJ> In particular, if <SafeMJ after=",">{"\\(\\deg(E)\\le e\\)"}</SafeMJ>{" "}
      then: <SafeMJ scrollable>{"\\[E(x)=E^\\star(x),\\quad Q(x)=Q^\\star(x).\\]"}</SafeMJ>
    </p>

    <Proof>
    <p className="mb-3">
      <em>Proof.</em> Let <SafeMJ>{"\\(Q(x),E(x)\\in\\mathbb{F}_q[x]\\)"}</SafeMJ> satisfy the initial conditions of the statement,
      and consider the following polynomial:
      <SafeMJ scrollable>{"\\[\\Delta(x)=Q(x)\\,E^\\star(x)-Q^\\star(x)\\,E(x),\\]"}</SafeMJ>
      which satisfies, for all <SafeMJ after=":">{"\\(i=1,\\ldots,n\\)"}</SafeMJ>
      <SafeMJ scrollable>{`
        \\[
        \\begin{aligned}
        \\Delta(\\alpha_i)
        &= Q(\\alpha_i)\\,E^\\star(\\alpha_i)-Q^\\star(\\alpha_i)\\,E(\\alpha_i) \\\\[5pt]
        &= r_i\\,E(\\alpha_i)\\,E^\\star(\\alpha_i)-r_i\\,E^\\star(\\alpha_i)\\,E(\\alpha_i) = 0.
        \\end{aligned}
        \\]
      `}
      </SafeMJ>
      Then, since the <SafeMJ>{"\\(n\\)"}</SafeMJ> <SafeMJ after="'s">{"\\(\\alpha_i\\)"}</SafeMJ> are distinct
      and <SafeMJ after=",">{"\\(\\deg(\\Delta)<t+k+e\\le 2t+k\\le n\\)"}</SafeMJ> using
      (as is now customary) lemma <SafeMJ after=",">{"\\((2.2)\\)"}</SafeMJ> we get <SafeMJ after=".">{"\\(\\Delta(x)\\equiv 0\\)"}</SafeMJ>{" "}
      And since <SafeMJ>{"\\(E^{\\star}(x)\\not\\equiv 0\\)"}</SafeMJ> in the field <SafeMJ after=",">{"\\(\\mathbb{F}_q[x]\\)"}</SafeMJ>{" "}
      we can divide through to get:
      <SafeMJ scrollable>{"\\[Q(x)=\\frac{Q^\\star(x)}{E^\\star(x)}\\,E(x)=m(x)\\,E(x).\\]"}</SafeMJ>
      So, for all <SafeMJ after=",">{"\\(i\\in\\{1,\\ldots,n\\}\\)"}</SafeMJ> we're left with the following equalities:
      <SafeMJ scrollable>{"\\[r_i\\,E(\\alpha_i)=Q(\\alpha_i)=m(\\alpha_i)\\,E(\\alpha_i),\\]"}</SafeMJ>
      so that if <SafeMJ after=",">{"\\(i\\in I\\)"}</SafeMJ> <SafeMJ>{"\\(m(\\alpha_i)\\neq r_i\\)"}</SafeMJ> and then necessarily <SafeMJ>{"\\(E(\\alpha_i)=0\\)"}</SafeMJ> (otherwise, we could divide both sides
      by <SafeMJ after=",">{"\\(E(\\alpha_i)\\neq 0\\)"}</SafeMJ> arriving at <SafeMJ after=").">{"\\(m(\\alpha_i)=r_i\\,!!!\\)"}</SafeMJ>{" "}
      That is, the <SafeMJ>{"\\(e\\)"}</SafeMJ> roots of <SafeMJ>{"\\(E^\\star(x)\\)"}</SafeMJ> are also roots of <SafeMJ after=".">{"\\(E(x)\\)"}</SafeMJ>{" "}
      So in the particular case where <SafeMJ after=",">{"\\(\\deg(E)\\le e\\)"}</SafeMJ> and since both polynomials are monic,
      we have <SafeMJ>{"\\(E(x)=E^\\star(x)\\)"}</SafeMJ> and hence <SafeMJ after=".">{"\\(Q(x)=Q^\\star(x)\\)"}</SafeMJ>
      <span className="qed"><SafeMJ>{"\\(\\square\\)"}</SafeMJ></span>
    </p>
    </Proof>

    <p className="mb-3">
      Wow, this clears up all our doubts. We now know that the system we set up at the beginning did indeed have the unique solution <SafeMJ after=".">{"\\((Q^\\star(x),E^\\star(x))\\)"}</SafeMJ>{" "}
      But on top of that, we don't care about not knowing how many errors have occurred. <b>We can put ourselves in the worst-case scenario (<SafeMJ after="),">{"\\(\\boldsymbol{e=t}\\)"}</SafeMJ> solve
      that "extended" system and we'll always get a solution <SafeMJ>{"\\(\\boldsymbol{(Q(x),E(x))}\\)"}</SafeMJ> that still works for us when it comes to recovering the original message</b>, since it will satisfy:
      <SafeMJ scrollable>{"\\[m(x)=\\frac{Q(x)}{E(x)}.\\]"}</SafeMJ>
      Mind you, in this second case, the polynomials obtained won't be the minimum-degree ones, as <SafeMJ>{"\\(Q^\\star(x)\\)"}</SafeMJ> and <SafeMJ>{"\\(E^\\star(x)\\)"}</SafeMJ> were.
      They'll come multiplied by a monic polynomial of degree <SafeMJ after=",">{"\\(t-e\\)"}</SafeMJ> but hey, when dividing they cancel out and job done.
    </p>

    <p className="mb-3">
      We've therefore managed to properly define an algorithm for the decoding task we were after, which is known
      in the literature as the <b>Berlekamp-Welch algorithm</b>. Yet another fancy word, yes, although what matters is that you've understood the simple,
      yet powerful, polynomial trick that makes it work.
    </p>

    <p className="mb-3">
      Let's see it in action, then. Let's take our reference example of <SafeMJ>{"\\(\\textrm{`}ouo\\textrm{'}\\)"}</SafeMJ> once again,
      where we said that <SafeMJ after=",">{"\\(n=q=5\\)"}</SafeMJ> <SafeMJ>{"\\(k=3\\)"}</SafeMJ> and <SafeMJ after=",">{"\\(\\alpha_i=i-1\\)"}</SafeMJ> for <SafeMJ after=",">{"\\(i=1,2,3,4,5\\)"}</SafeMJ>{" "}
      and suppose the following transmission error occurs:
      <SafeMJ scrollable>{"\\[m=(3,4,3)\\longrightarrow c=(3,0,3,2,2)\\longrightarrow r=(3,0,2,2,2).\\]"}</SafeMJ>
      Since in this case <SafeMJ after=",">{"\\(t=1\\)"}</SafeMJ> we're looking for two polynomials in <SafeMJ after=":">{"\\(\\mathbb{F}_5[x]\\)"}</SafeMJ>
      <SafeMJ scrollable>{"\\[Q(x)=q_3x^3+q_2x^2+q_1x+q_0,\\quad E(x)=x+e_0,\\]"}</SafeMJ>
      such that:
      <SafeMJ scrollable>{`
        \\[
        \\begin{cases}
        Q(0)=3\\,E(0) & \\\\
        Q(1)=0 & \\\\
        Q(2)=2\\,E(2) & \\\\
        Q(3)=2\\,E(3) & \\\\
        Q(4)=2\\,E(4)
        \\end{cases}
        \\longrightarrow
        \\begin{cases}
        q_0+2e_0=0 & \\\\
        q_3+q_2+q_1+q_0=0 & \\\\
        3q_3+4q_2+2q_1+q_0+3e_0=4 & \\\\
        2q_3+4q_2+3q_1+q_0+3e_0=1 & \\\\
        4q_3+q_2+4q_1+q_0+3e_0=3
        \\end{cases}
        \\]
      `}</SafeMJ>
      Solving by Gauss-Jordan (I'll leave it to you to finish), we get:
      <SafeMJ scrollable>{`
        \\[
        \\left[
          \\begin{array}{ccccc|c}
            0&0&0&1&2&0\\\\ 1&1&1&1&0&0\\\\ 3&4&2&1&3&4\\\\ 2&4&3&1&3&1\\\\ 4&1&4&1&3&3
          \\end{array}
        \\right]
        \\longrightarrow
        \\left[
          \\begin{array}{ccccc|c}
            1&1&1&1&0&0\\\\ 0&1&4&3&3&4\\\\ 0&2&1&4&3&1\\\\ 0&0&0&1&2&0\\\\ 0&3&3&4&2&1
          \\end{array}
        \\right]
        \\longrightarrow
        \\left[
          \\begin{array}{ccccc|c}
            1&0&2&3&2&1\\\\ 0&1&4&3&3&4\\\\ 0&0&3&3&2&3\\\\ 0&0&0&1&2&0\\\\ 0&0&1&0&3&4
          \\end{array}
        \\right]
        \\]
      `}</SafeMJ>
      <SafeMJ scrollable>{`
        \\[
        \\longrightarrow
        \\left[
          \\begin{array}{ccccc|c}
            1&0&0&3&1&3\\\\ 0&1&0&3&1&3\\\\ 0&0&1&0&3&4\\\\ 0&0&0&1&2&0\\\\ 0&0&0&3&3&1
          \\end{array}
        \\right]
        \\longrightarrow
        \\cdots
        \\longrightarrow
        \\left[
          \\begin{array}{ccccc|c}
            1&0&0&0&0&3\\\\ 0&1&0&0&0&3\\\\ 0&0&1&0&0&0\\\\ 0&0&0&1&0&4\\\\ 0&0&0&0&1&3
          \\end{array}
        \\right]
        \\]
      `}</SafeMJ>
      So the solution polynomials turn out to be:
      <SafeMJ scrollable>{"\\[Q(x)=3x^3+3x^2+4,\\quad E(x)=x+3,\\]"}</SafeMJ>
      and we recover the original message by dividing:
      <SafeMJ scrollable>{"\\[m(x)=\\frac{Q(x)}{E(x)}=\\frac{3x^3+3x^2+4}{x+3}=3x^2+4x+3.\\]"}</SafeMJ>
      Neat, and above all easy, right? Also, note that in this case we have <SafeMJ after=",">{"\\(e=t\\)"}</SafeMJ> so the polynomials found are
      actually <SafeMJ>{"\\(Q^\\star(x)\\)"}</SafeMJ> and <SafeMJ after=",">{"\\(E^\\star(x)\\)"}</SafeMJ> and therefore we know that the root <SafeMJ>{"\\(\\alpha_2=2\\)"}</SafeMJ> of <SafeMJ>{"\\(E(x)\\)"}</SafeMJ>{" "}
      tells us that the only error occurred in the third coordinate of the word. Bingo!
    </p>

    <p className="mb-3">
      If, on the other hand, the word hadn't suffered any transmission error, that is, <SafeMJ after=",">{"\\(0=e<t=1\\)"}</SafeMJ> I invite you to check that the corresponding system
      gives the following solutions:
      <SafeMJ scrollable>{"\\[Q(x)=3x^3+x^2+4x+2,\\quad E(x)=x+4.\\]"}</SafeMJ>
      Obviously we can still recover <SafeMJ>{"\\(m(x)\\)"}</SafeMJ> by dividing <SafeMJ>{"\\(Q(x)\\)"}</SafeMJ> by <SafeMJ after=",">{"\\(E(x)\\)"}</SafeMJ>{" "}
      it's just that now <SafeMJ>{"\\(E(x)\\)"}</SafeMJ> doesn't coincide with the error locator polynomial and, in fact, includes a <em>spurious root</em> (a fake one): <SafeMJ after=".">{"\\(\\alpha_1=1\\)"}</SafeMJ>
    </p>

    <p className="mb-3">
      And finally, to finish discussing all the cases. What would have happened if <SafeMJ after="?">{"\\(e>t\\)"}</SafeMJ> First of all, note that then <SafeMJ>{"\\(E^\\star(x)\\)"}</SafeMJ> would have
      degree greater than <SafeMJ after=",">{"\\(t\\)"}</SafeMJ> so it would no longer be guaranteed that the system Berlekamp-Welch solves had a solution. Even if it had one,
      the inequality <SafeMJ>{"\\(e\\le t\\)"}</SafeMJ> was essential in the proof of proposition <SafeMJ after=",">{"\\((5.4)\\)"}</SafeMJ> both to prove
      that <SafeMJ>{"\\(E(x)\\)"}</SafeMJ> divides <SafeMJ>{"\\(Q(x)\\)"}</SafeMJ> and for that quotient to coincide with the original message polynomial. Without that condition,
      then, any of these three scenarios could ruin our decoding. For example, I'll leave it as an exercise for you to study the case
      of having received the word <SafeMJ after=",">{"\\(r=(4,0,2,2,2)\\)"}</SafeMJ> which now contains two errors.
    </p>

    <p className="mb-3">
      Lastly. What if the received word, as we mentioned at the end of the previous section, also contained <SafeMJ>{"\\(b\\)"}</SafeMJ> erasures? Well, as long as the
      inequality <SafeMJ>{"\\((4.3)\\)"}</SafeMJ> still held, Berlekamp-Welch would keep working. We would simply discard the <SafeMJ>{"\\(b\\)"}</SafeMJ> erased positions
      and set up the system with the remaining <SafeMJ>{"\\(n-b\\)"}</SafeMJ> equations. In other words, it would be like the standard case,
      just with an error-correcting capability of <SafeMJ>{"\\(t'=\\lfloor(n-b-k)/2\\rfloor\\)"}</SafeMJ> errors.
    </p>

    <p className="mb-3">
      As for the computational complexity of Berlekamp-Welch, note that the algorithm only consists of:
      <ul className="list-decimal list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1 marker:font-bold">Solving a linear system with <SafeMJ>{"\\(n\\)"}</SafeMJ> equations and <SafeMJ>{"\\(2t+k\\le n\\)"}</SafeMJ> unknowns,
        which has a cost of <SafeMJ>{"\\(O(n^3)\\)"}</SafeMJ> in general, as you know.
        </li>
        <li className="mb-1 marker:font-bold">Dividing a polynomial of degree <SafeMJ>{"\\(t+k-1\\)"}</SafeMJ> by another of degree <SafeMJ after=".">{"\\(t\\)"}</SafeMJ> Since for
        each of the <SafeMJ>{"\\(k\\)"}</SafeMJ> coefficients of the quotient polynomial we need <SafeMJ>{"\\(t+1\\)"}</SafeMJ> multiplications and additions, the number of
        operations will be <SafeMJ after=",">{"\\(O(k(t+1))=O(n^2)\\)"}</SafeMJ> which is negligible compared to the previous cost.
        </li>
      </ul>
      Basically, the total complexity is <SafeMJ after=".">{"\\(O(n^3)\\)"}</SafeMJ> Not especially good, to be honest, but at least
      it's reasonable compared to the brute force we laid out at the beginning. And besides, you can't deny that the algorithm is pretty elegant, eh?
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">6. Reed-Solomon in practice</h2>

    <p className="mb-3">
      So far we've described Reed-Solomon just as it was presented in the original paper, that is, from the evaluation point of view: a message is a polynomial and the
      codeword is obtained by evaluating it at different elements of the field. This construction is especially handy for understanding the code mathematically and building elementary algorithms like Berlekamp-Welch.
      However, to implement certain Reed-Solomon configurations on real devices, there's another, more useful way of describing the code.
    </p>

    <p className="mb-3">
      To do so, let's bring back our beloved Galois Theory and consider a primitive element <SafeMJ>{"\\(\\alpha\\)"}</SafeMJ> of the finite field <SafeMJ after=".">{"\\(\\mathbb{F}_q\\)"}</SafeMJ>{" "}
      Then, if we take <SafeMJ>{"\\(1,\\alpha,\\dots,\\alpha^{n-1}\\)"}</SafeMJ> as evaluation points, the classic Reed-Solomon code becomes:
      <SafeMJ scrollable>{"\\[\\mathcal{C_{\\mathrm{eval}}}:=\\left\\{(m(1),m(\\alpha),\\dots,m(\\alpha^{n-1}))\\in\\mathbb{F}_q^n:m(x)\\in\\mathbb{F}_q[x]_{<k}\\right\\}.\\]"}</SafeMJ>
      It's precisely this idea of taking consecutive powers of a primitive element as evaluation points that will allow us to define a new way of building Reed-Solomon.
      Also, to guarantee that these evaluation points are distinct, we'll require <SafeMJ after=",">{"\\(n\\le q-1\\)"}</SafeMJ> since
      let's not forget that <SafeMJ after=",">{"\\(\\alpha^{q-1}=1\\)"}</SafeMJ> as <SafeMJ>{"\\(\\alpha\\)"}</SafeMJ> has order <SafeMJ after=".">{"\\(q-1\\)"}</SafeMJ>
    </p>
    
    <p className="mb-3">
      With that said, we move on to define the <b>generator polynomial</b> associated with this configuration as:
      <SafeMJ scrollable>{"\\[g(x):=\\prod_{i=1}^{n-k}(x-\\alpha^i)\\in\\mathbb{F}_q[x],\\]"}</SafeMJ>
      which, as we can see, has degree <SafeMJ>{"\\(n-k\\)"}</SafeMJ> and is monic.
    </p>

    <p className="mb-3">
      Then, the <b>code generated by <SafeMJ>{"\\(\\boldsymbol{g(x)}\\)"}</SafeMJ></b> is defined as:
      <SafeMJ scrollable>{"\\[\\mathcal{C}_g:=\\left\\{(c_0,\\dots,c_{n-1})\\in\\mathbb{F}_q^n:g(x)\\mid c(x)=\\sum_{i=0}^{n-1} c_i x^i\\right\\},\\]"}</SafeMJ>
      that is, as the coefficient vectors of all the polynomials of <SafeMJ>{"\\(\\mathbb{F}_q[x]\\)"}</SafeMJ> of degree less than <SafeMJ>{"\\(n\\)"}</SafeMJ> that are multiples of <SafeMJ after=".">{"\\(g(x)\\)"}</SafeMJ>
    </p>
    
    <p className="mb-3">
      Repeating an argument similar to the one we made with the classic version of Reed-Solomon, it's easy to check that the map:
      <SafeMJ scrollable>{"\\[\\psi:\\mathbb{F}_q[x]_{<k}\\longrightarrow\\mathbb{F}_q^n,\\quad m(x)\\longmapsto \\textrm{coeff}(m(x)g(x))\\]"}</SafeMJ>
      is linear and injective, so <SafeMJ>{"\\(\\mathcal{C}_g\\)"}</SafeMJ> is a linear code of dimension <SafeMJ>{"\\(k\\)"}</SafeMJ> and length <SafeMJ after=".">{"\\(n\\)"}</SafeMJ>{" "}
      In fact, if we write:
      <SafeMJ scrollable>{"\\[g(x)=g_0+g_1x+\\cdots+g_{n-k-1}x^{n-k-1}+x^{n-k},\\]"}</SafeMJ>
      its generator matrix turns out to be:
      <SafeMJ scrollable>{`\\[G=
      \\begin{pmatrix}
      g_0 & g_1 & \\cdots & g_{n-k-1} & 1 & 0 & \\cdots & 0\\\\
      0 & g_0 & \\cdots & g_{n-k-2} & g_{n-k-1} & 1 & \\cdots & 0\\\\
      \\vdots & \\vdots & \\ddots & \\ddots & \\ddots & \\ddots & \\ddots & \\vdots\\\\
      0 & 0 & \\cdots & g_0 & g_1 & \\cdots & g_{n-k-1} & 1
      \\end{pmatrix}.
      \\]`}</SafeMJ>
      since its rows are precisely the coefficient vectors of <SafeMJ after=",">{"\\(g(x),xg(x),\\dots,x^{k-1}g(x)\\)"}</SafeMJ> respectively.
    </p>

    <p className="mb-3">
      Right, we've managed to encode the polynomial <SafeMJ>{"\\(m(x)\\)"}</SafeMJ> in a
      new way, but what does this have to do with our Reed-Solomon? At first glance, <SafeMJ>{"\\(\\mathcal{C}_g\\)"}</SafeMJ> looks like a code
      completely different from the one we've been handling so far, right? Not at all. But before proving it,
      we'll need a small identity.
    </p>

    <p className="mb-3">
      <b>Lemma 6.1.</b> Let <SafeMJ>{"\\(\\alpha\\)"}</SafeMJ> be an element of order <SafeMJ>{"\\(n\\)"}</SafeMJ> in <SafeMJ after=".">{"\\(\\mathbb{F}_q\\)"}</SafeMJ>{" "}
      Then, for any integer <SafeMJ after=",">{"\\(t\\)"}</SafeMJ> we have:
      <SafeMJ scrollable>{"\\[\\sum_{j=0}^{n-1}\\alpha^{tj}=\\begin{cases}n,&n\\mid t,\\\\0,&n\\nmid t.\\end{cases}\\]"}</SafeMJ>
    </p>

    <Proof>
      <p className="mb-3">
        <em>Proof.</em> If <SafeMJ after=",">{"\\(n\\mid t\\)"}</SafeMJ> then <SafeMJ>{"\\(\\alpha^t=1\\)"}</SafeMJ> and,
        therefore, <SafeMJ>{"\\(\\sum_{j=0}^{n-1}\\alpha^{tj}=\\sum_{j=0}^{n-1}1=n.\\)"}</SafeMJ>
      </p>

      <p className="mb-3">
        If, on the other hand, <SafeMJ after=",">{"\\(n\\nmid t\\)"}</SafeMJ> then <SafeMJ>{"\\(\\alpha^t\\neq1\\)"}</SafeMJ> and
        we can apply the geometric series formula to get:
        <SafeMJ scrollable>{"\\[\\sum_{j=0}^{n-1}\\alpha^{tj}=\\frac{(\\alpha^{t})^n-1}{\\alpha^t-1}=\\frac{1-1}{\\alpha^t-1}=0.\\tag*{$\\square$}\\]"}</SafeMJ>
      </p>
    </Proof>

    <p className="mb-3">
      With this auxiliary result, we can now go on to prove that both constructions actually had a great deal to do with each other.
    </p>

    <p className="mb-3">
      <b>Proposition 6.2.</b> If <SafeMJ after=",">{"\\(n=q-1\\)"}</SafeMJ> then <SafeMJ after=".">{"\\(\\mathcal{C}_{\\mathrm{eval}}=\\mathcal{C}_g\\)"}</SafeMJ>
    </p>

    <Proof>
      <p className="mb-3">
        <em>Proof.</em> Since both codes are linear and have the same dimension, it's enough to prove
        that one of them is contained in the other. In particular, let's see
        that <SafeMJ after=".">{"\\(\\mathcal{C}_{\\mathrm{eval}}\\subseteq\\mathcal{C}_g\\)"}</SafeMJ>
      </p>

      <p className="mb-3">
        The case <SafeMJ>{"\\(n=k\\)"}</SafeMJ> is trivial, since then <SafeMJ>{"\\(g(x)=1\\)"}</SafeMJ> and <SafeMJ after=".">{"\\(\\mathcal{C}_{\\mathrm{eval}}=\\mathcal{C}_g=\\mathbb{F}_q^n\\)"}</SafeMJ>{" "}
        So we'll assume <SafeMJ after=".">{"\\(n>k\\)"}</SafeMJ>
      </p>

      <p className="mb-3">
        Then, let <SafeMJ after=";">{"\\(c=(c_0,\\dots,c_{n-1})\\in\\mathcal{C}_{\\mathrm{eval}}\\)"}</SafeMJ> by
        definition, there exists a polynomial <SafeMJ>{"\\(m(x)=\\sum_{i=0}^{k-1}m_i x^i\\)"}</SafeMJ> such that:
        <SafeMJ scrollable>{"\\[m(\\alpha^j)=c_j,\\quad \\forall j=0,\\dots,n-1.\\]"}</SafeMJ>
      </p>

      <p className="mb-3">
        Since <SafeMJ>{"\\(c\\in\\mathcal{C}_g\\)"}</SafeMJ> if and only if the polynomial <SafeMJ>{"\\(c(x):=\\sum_{j=0}^{n-1}c_jx^j\\)"}</SafeMJ> is divisible by <SafeMJ after=",">{"\\(g(x)\\)"}</SafeMJ> and the
        latter has <SafeMJ>{"\\(\\alpha,\\alpha^2,\\dots,\\alpha^{n-k}\\)"}</SafeMJ> as its only roots, it's enough to see that:
        <SafeMJ scrollable>{"\\[c(\\alpha^s)=0,\\quad\\forall s=1,\\dots,n-k.\\]"}</SafeMJ>
      </p>

      <p className="mb-3">
        Indeed, for any of those values of <SafeMJ after=":">{"\\(s\\)"}</SafeMJ>
        <SafeMJ scrollable>{`
          \\[\\begin{aligned}c(\\alpha^s)&=\\sum_{j=0}^{n-1}c_j\\alpha^{sj}=\\sum_{j=0}^{n-1}m(\\alpha^j)\\alpha^{sj}\\\\
          &=\\sum_{j=0}^{n-1}\\left(\\sum_{i=0}^{k-1}m_i\\alpha^{ij}\\right)\\alpha^{sj}\\\\
          &=\\sum_{i=0}^{k-1}m_i\\sum_{j=0}^{n-1}\\alpha^{(i+s)j}.\\end{aligned}\\]
        `}</SafeMJ>
      </p>

      <p className="mb-3">
        Since <SafeMJ after=",">{"\\(1\\leq i+s\\leq n-1\\)"}</SafeMJ> then <SafeMJ after=".">{"\\(n\\nmid(i+s)\\)"}</SafeMJ> Therefore, since <SafeMJ>{"\\(\\alpha\\)"}</SafeMJ> has
        order <SafeMJ after=",">{"\\(n=q-1\\)"}</SafeMJ> we can use lemma <SafeMJ>{"\\((6.1)\\)"}</SafeMJ> to conclude that:
        <SafeMJ scrollable>{"\\[\\sum_{j=0}^{n-1}\\alpha^{(i+s)j}=0.\\tag*{$\\square$}\\]"}</SafeMJ>
      </p>
    </Proof>

    <p className="mb-3">
      All in all, it turns out that both constructions describe exactly the same code. Powerful stuff. Mind you, they don't necessarily establish
      the same correspondence between messages and codewords. That is, the same message can give rise to different
      words depending on the method used; it's just that when going through all possible messages, the set of resulting words
      will indeed be the same.
    </p>

    <p className="mb-3">
      As for the hypothesis of the proposition, which I know more than one of you will be thinking about, it's true that the case we've proved
      corresponds to a particularly convenient choice of <SafeMJ after=",">{"\\(n\\)"}</SafeMJ> of the evaluation points and of the generator polynomial.
      Everything has fitted together perfectly, to be honest.
    </p>

    <p className="mb-3">
      In other common configurations, for example with <SafeMJ after=",">{"\\(n<q-1\\)"}</SafeMJ> with the index of <SafeMJ>{"\\(g(x)\\)"}</SafeMJ> running over <SafeMJ>{"\\(i=0,\\ldots,n-k-1\\)"}</SafeMJ> or
      choosing the evaluation points in a different order,
      the previous equality, unfortunately, no longer holds in general. However, and here's the key, in these variants <SafeMJ>{"\\(\\mathcal{C}_g\\)"}</SafeMJ> is
      what's called <b>monomially equivalent</b> to the evaluation Reed-Solomon.
      That is, we can transform one code into the other simply by suitably reordering and/or scaling the coordinates of the words,
      which requires adapting the previous proof a little with additional factors and permutations.
      If you think about it, this means both codes are "essentially" the same, since the Hamming distance between words is preserved.
    </p>

    <p className="mb-3">
      Great, so we now have a new way of expressing Reed-Solomon. But why is this variant better for implementation?
      To see why, let's make a small change to the encoding method. Instead of multiplying a message <SafeMJ>{"\\(m(x)\\)"}</SafeMJ> by <SafeMJ after=",">{"\\(g(x)\\)"}</SafeMJ>{" "}
      we consider the polynomial <SafeMJ>{"\\(m(x)x^{n-k}\\)"}</SafeMJ> and divide it by <SafeMJ after=":">{"\\(g(x)\\)"}</SafeMJ>
      <SafeMJ scrollable>{"\\[m(x)x^{n-k}=q(x)g(x)+s(x),\\quad\\textnormal{with }\\deg(s)<n-k.\\]"}</SafeMJ>
      Then, note that:
      <SafeMJ scrollable>{"\\[c(x):=m(x)x^{n-k}-s(x),\\tag{6.3}\\]"}</SafeMJ>
      represents a word of <SafeMJ after=",">{"\\(\\mathcal{C}_g\\)"}</SafeMJ> since it has degree less than <SafeMJ>{"\\(n\\)"}</SafeMJ> and is a multiple of the generator polynomial.
      But what's more, since <SafeMJ after=",">{"\\(\\deg(s)<n-k\\)"}</SafeMJ> subtracting <SafeMJ>{"\\(s(x)\\)"}</SafeMJ> from <SafeMJ>{"\\(m(x)x^{n-k}\\)"}</SafeMJ> won't
      change the coefficients of degree <SafeMJ>{"\\(n-k\\)"}</SafeMJ> or higher, so the codeword will literally contain
      the <SafeMJ>{"\\(k\\)"}</SafeMJ> symbols of the message and add <SafeMJ>{"\\(n-k\\)"}</SafeMJ> redundancy symbols:
      <SafeMJ scrollable>{"\\[c=(-s_0,\\ldots,-s_{n-k-1},m_0,\\ldots,m_{k-1}).\\]"}</SafeMJ>
      This kind of encoding, in which the original message symbols appear directly in the encoded word,
      while the rest contain only the redundant information needed for error correction, is called,
      by the way, <b>systematic</b>.
    </p>

    <p className="mb-3">
      In fact, it follows trivially from this property that the new encoding we've just defined is injective, since if two messages differ
      in some component, then their respective codewords (which include those components) will necessarily be different.
      This in turn implies that we haven't lost any of the <SafeMJ>{"\\(q^k\\)"}</SafeMJ> words of <SafeMJ>{"\\(\\mathcal{C}_g\\)"}</SafeMJ> and
      that we're therefore still generating exactly the same code as before.
    </p>

    <p className="mb-3">
      As for the practicality of this new version of Reed-Solomon, I think by now it's fairly obvious why using the generator polynomial is preferred over direct evaluation.
      First, it's very convenient for the receiver that the original message appears in the codeword, since this avoids having to perform additional
      transformations, like the Lagrange interpolation we did with the original definition, to recover the message.
    </p>

    <p className="mb-3">
      Second. This new encoding basically boils down to computing:
      <SafeMJ scrollable>{"\\[s(x)=m(x)x^{n-k}\\bmod g(x)\\tag{6.4},\\]"}</SafeMJ>
      which is nothing more than taking the remainder of dividing a polynomial of degree less than <SafeMJ>{"\\(n\\)"}</SafeMJ> by another of degree <SafeMJ after=",">{"\\(n-k\\)"}</SafeMJ> which
      involves <SafeMJ>{"\\(O(k(n-k))\\)"}</SafeMJ> operations, as we already explained in the previous section. With evaluation, on the other hand, we had to do <SafeMJ>{"\\(k\\)"}</SafeMJ> multiplications
      and <SafeMJ>{"\\(k\\)"}</SafeMJ> additions for each of the <SafeMJ>{"\\(n\\)"}</SafeMJ> evaluation points, so the total number of operations was somewhat higher: <SafeMJ after=".">{"\\(O(kn)\\)"}</SafeMJ>{" "}
      But even better is the fact that there are super easy circuits to implement in hardware that massively
      speed up the computation of <SafeMJ after=".">{"\\(s(x)\\)"}</SafeMJ> A classic example is the LFSR (Linear Feedback Shift Register), made up of a chain of registers in which the
      state of each cycle is used to compute the state of the next. In particular, the registers store the coefficients of the partial remainder of the division, and these are updated with each
      input symbol of <SafeMJ after=".">{"\\(m(x)x^{n-k}\\)"}</SafeMJ> The connections and the intermediate feedback operations are determined, as you'll have guessed, by <SafeMJ after=",">{"\\(g(x)\\)"}</SafeMJ> which
      is fixed.
    </p>

    <p className="mb-3">
      And lastly, and probably most importantly, this new approach to Reed-Solomon is especially useful for the decoding stage.
      To see this, suppose we receive a polynomial <SafeMJ after=",">{"\\(r(x)=c(x)+e(x)\\)"}</SafeMJ> where <SafeMJ>{"\\(c(x)\\)"}</SafeMJ> is the polynomial
      corresponding to a word of <SafeMJ>{"\\(\\mathcal{C}_g\\)"}</SafeMJ> and <SafeMJ>{"\\(e(x)\\)"}</SafeMJ> represents the error introduced
      during transmission. Then, evaluating the received polynomial at the roots of the generator polynomial and bearing in mind that <SafeMJ>{"\\(g(x)\\)"}</SafeMJ> divides <SafeMJ after=",">{"\\(c(x)\\)"}</SafeMJ>{" "}
      we get the so-called <b>syndromes</b>:
      <SafeMJ scrollable>{"\\[S_i:=r(\\alpha^i)=\\underbrace{c(\\alpha^i)}_{\\displaystyle 0}+e(\\alpha^i)=e(\\alpha^i),\\quad \\forall i=1,\\ldots,n-k.\\]"}</SafeMJ>
      The powerful thing, as you can see, is that the syndromes depend only on the error introduced during transmission, so that if all of them are zero,
      we can state that the received word is valid. This very property is precisely what many of the Reed-Solomon decoding algorithms
      used today exploit, and they actually manage to reduce the computational complexity of recovering the message to <SafeMJ after="!">{"\\(O(n^2)\\)"}</SafeMJ> To name a few,
      there's <b>Berlekamp-Massey</b>, which builds the error locator polynomial iteratively, or also <b>Sugiyama's algorithm</b>, based on
      the extended Euclidean algorithm. As always, I recommend you look into them a bit more if you're interested...
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">7. QR codes</h2>

    <p className="mb-3">
      As we announced in the introduction, a very well-known practical case of Reed-Solomon is QR codes. Those little black and white
      squares are definitely everywhere by now, and they even sneak them onto restaurant menus.
      At least, thanks to all the theory we've developed, understanding their error-correcting capability is going to be a piece of cake.
      All the specifications are, in fact, laid out in perfect detail in the ISO/IEC 18004 standard.
    </p>

    <p className="mb-3">
      First things first. QR codes use Reed-Solomon over <SafeMJ after=",">{"\\(\\mathbb{F}_{256}\\cong\\mathbb{F}_2[z]/\\langle z^8+z^4+z^3+z^2+1\\rangle\\)"}</SafeMJ> that is, the field with 256 elements.
      I'll leave it as an exercise to prove that the polynomial <SafeMJ>{"\\(f(z)=z^8+z^4+z^3+z^2+1\\)"}</SafeMJ> is irreducible over <SafeMJ after=",">{"\\(\\mathbb{F}_2\\)"}</SafeMJ> which is essential,
      as you know, to be able to apply proposition <SafeMJ after=".">{"\\((1.2)\\)"}</SafeMJ> It's a bit tedious, but in the end it's simply a matter of checking that <SafeMJ>{"\\(f(z)\\)"}</SafeMJ> isn't divisible by any
      irreducible factor of degree less than or equal to 4, of which there aren't that many.
    </p>

    <p className="mb-3">
      Choosing this field and not another is actually quite key for our use case. Recall from the first section of the article that each element of this field could be represented by a polynomial, in this case, of degree less than 8 and with coefficients in <SafeMJ after=".">{"\\(\\mathbb{F}_2\\)"}</SafeMJ>{" "}
      Or, in other words, by a sequence of 8 bits, that is... a byte. Aaaaand there you go.
    </p>

    <p className="mb-3">
      In fact, to simplify the notation even further, it's common to use decimal instead of binary. For example, <SafeMJ after=".">{"\\(p(z)=z^4+z^3+1\\equiv 11001_2=25\\)"}</SafeMJ>{" "}
      But careful, let's never forget that we're not working with ordinary integers. That is, <SafeMJ>{"\\(p(z)^2\\)"}</SafeMJ> is by no means <SafeMJ after=",">{"\\(25^2\\bmod 256=113\\)"}</SafeMJ>{" "}
      but rather you have to expand and reduce modulo <SafeMJ after=",">{"\\(f(z)\\)"}</SafeMJ> remember:
      <SafeMJ scrollable>{"\\[25^2\\equiv(z^4+z^3+1)^2=z^8+z^6+1\\equiv z^6+z^4+z^3+z^2\\equiv 1011100_2\\equiv 92.\\]"}</SafeMJ>
    </p>

    <p className="mb-3">
      On the other hand, to turn the character string to be encoded into a set of bytes, the standard allows several alternatives, depending on the symbols we want to include.
      For example, <b>byte mode</b>, one of the most widely used, turns each character of a typical text string (Latin alphabet, diacritics and some special characters)
      into a byte following the ISO-8859-1 standard, which is nothing more than an arbitrary conversion table.
      For example, with this mode the URL:
      <SafeMJ scrollable>{"\\[\\texttt{https://jaimegimillo.me}\\]"}</SafeMJ>
      turns into the following sequence of bytes:

    <div className="flex flex-col md:flex-row gap-4 m-5">
      <table className="table-auto border-collapse border border-gray-300 dark:border-gray-700 w-full text-md">
        <thead className="bg-gray-100 dark:bg-gray-800">
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">
              #
            </th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">
              Character
            </th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">
              Decimal
            </th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">
              Binary
            </th>
          </tr>
        </thead>

        <tbody className="bg-white dark:bg-gray-900">
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">1</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{h}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(104\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101000\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">2</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{t}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(116\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01110100\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">3</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{t}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(116\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01110100\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">4</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{p}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(112\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01110000\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">5</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{s}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(115\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01110011\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">6</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{:}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(58\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(00111010\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">7</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{/}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(47\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(00101111\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">8</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{/}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(47\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(00101111\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">9</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{j}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(106\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101010\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">10</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{a}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(97\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01100001\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">11</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{i}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(105\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101001\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">12</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{m}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(109\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101101\\)"}</SafeMJ></td>
          </tr>
        </tbody>
      </table>

      <table className="table-auto border-collapse border border-gray-300 dark:border-gray-700 w-full text-md">
        <thead className="bg-gray-100 dark:bg-gray-800">
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">
              #
            </th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">
              Character
            </th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">
              Decimal
            </th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">
              Binary
            </th>
          </tr>
        </thead>

        <tbody className="bg-white dark:bg-gray-900">
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">13</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{e}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(101\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01100101\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">14</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{g}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(103\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01100111\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">15</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{i}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(105\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101001\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">16</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{m}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(109\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101101\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">17</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{i}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(105\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101001\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">18</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{l}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(108\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101100\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">19</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{l}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(108\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101100\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">20</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{o}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(111\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101111\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">21</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{.}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(46\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(00101110\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">22</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{m}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(109\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101101\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">23</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{e}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(101\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01100101\\)"}</SafeMJ></td>
          </tr>
        </tbody>
      </table>
    </div>
      There are, obviously, other conversion modes, such as numeric mode, for encoding digits only, or even one for kanji (Japanese characters).
    </p>

    <p className="mb-3">
      Once we have the sequence of <SafeMJ>{"\\(k\\)"}</SafeMJ> bytes we want to encode in the QR, it's time to add the redundancy.
      To do so, the standard says to use the systematic encoding we saw in the previous section, that is, <SafeMJ after=",">{"\\((6.3)\\)"}</SafeMJ> using as generator polynomial:
      <SafeMJ scrollable>{"\\[g(x)=\\prod_{i=0}^{n-k-1}(x-\\alpha^i),\\]"}</SafeMJ>
      with <SafeMJ>{"\\(\\alpha=2\\)"}</SafeMJ> (<SafeMJ after=",">{"\\(z\\)"}</SafeMJ> seen as a polynomial) as the primitive element of <SafeMJ after=".">{"\\(\\mathbb{F}_{256}\\)"}</SafeMJ>{" "}
      Checking that this <SafeMJ>{"\\(\\alpha\\)"}</SafeMJ> has order 255 looks extremely long and boring, but it's actually not that bad if we use Lagrange's Theorem for groups,
      which tells us that:
      <SafeMJ scrollable>{"\\[o(\\alpha)\\mid |\\mathbb{F}_{256}^\\times|=255=3\\cdot 5 \\cdot 17.\\]"}</SafeMJ>
      Then, it's enough to check (the last thing I'll leave you as an exercise, sorry) that:
      <SafeMJ scrollable>{`
        \\[
        \\begin{aligned}
        \\alpha^{3\\cdot 5} &= z^{15} = z^5+z^2+z \\neq 1,\\\\[4pt]
        \\alpha^{3\\cdot 17} &= z^{51} = z^3+z \\neq 1,\\\\[4pt]
        \\alpha^{5\\cdot 17} &= z^{85} = z^7+z^6+z^4+z^2+z \\neq 1.
        \\end{aligned}
        \\]
      `}</SafeMJ>
      If you don't want to die trying, I recommend you first compute the powers <SafeMJ after=",">{"\\(z^{8},z^{16},z^{32},z^{64}\\)"}</SafeMJ>{" "}
      taking advantage of the fact that the cross term of <SafeMJ>{"\\((a+b)^2\\)"}</SafeMJ> vanishes in <SafeMJ after=".">{"\\(\\mathbb{F}_2\\)"}</SafeMJ>
    </p>

    <p className="mb-3">
      Next, more things we need to settle. Which <SafeMJ>{"\\(n\\)"}</SafeMJ> and <SafeMJ>{"\\(k\\)"}</SafeMJ> do we take? The standard
      defines up to 40 QR versions, each with a greater total storage capacity (<SafeMJ after=")">{"\\(n\\)"}</SafeMJ> than the previous one.
      Moreover, for each of these versions, there are 4 error-correction levels, which determine how that fixed <SafeMJ>{"\\(n\\)"}</SafeMJ> is split between <SafeMJ>{"\\(k\\)"}</SafeMJ> and <SafeMJ after=".">{"\\(n-k\\)"}</SafeMJ>{" "}
      In particular, each level sets the (very) approximate percentage <SafeMJ>{"\\(t/n\\)"}</SafeMJ> of the word's bytes that can be recovered:
      <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1"><b>Level L</b> (Low): <SafeMJ>{"\\(\\approx 7\\%\\)"}</SafeMJ> of bytes correctable.</li>
        <li className="mb-1"><b>Level M</b> (Medium): <SafeMJ after=".">{"\\(\\approx 15\\%\\)"}</SafeMJ></li>
        <li className="mb-1"><b>Level Q</b> (Quartile): <SafeMJ after=".">{"\\(\\approx 25\\%\\)"}</SafeMJ></li>
        <li className="mb-1"><b>Level H</b> (High): <SafeMJ after=".">{"\\(\\approx 30\\%\\)"}</SafeMJ></li>
      </ul>
      Here's, for example, that split for the first 3 QR versions:
    </p>

    <div className="flex flex-col md:flex-row justify-center gap-6 m-5 overflow-x-auto">
      {/* V1 */}
      <table className="table-auto border-collapse border border-gray-300 dark:border-gray-700 min-w-[220px] text-md">
        <thead>
          <tr>
            <th
              colSpan={4}
              className="border border-gray-300 dark:border-gray-700 px-3 py-2 text-center bg-gray-100 dark:bg-gray-800"
            >
              V1
            </th>
          </tr>
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-left">
              Level
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(n\\)"}</SafeMJ>
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(k\\)"}</SafeMJ>
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(n-k\\)"}</SafeMJ>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">L</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">26</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">19</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">7</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">M</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">26</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">16</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">10</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">Q</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">26</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">13</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">13</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">H</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">26</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">9</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">17</td>
          </tr>
        </tbody>
      </table>

      {/* V2 */}
      <table className="table-auto border-collapse border border-gray-300 dark:border-gray-700 min-w-[220px] text-md">
        <thead>
          <tr>
            <th
              colSpan={4}
              className="border border-gray-300 dark:border-gray-700 px-3 py-2 text-center bg-gray-100 dark:bg-gray-800"
            >
              V2
            </th>
          </tr>
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-left">
              Level
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(n\\)"}</SafeMJ>
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(k\\)"}</SafeMJ>
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(n-k\\)"}</SafeMJ>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">L</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">44</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">34</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">10</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">M</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">44</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">28</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">16</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">Q</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">44</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">22</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">22</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">H</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">44</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">16</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">28</td>
          </tr>
        </tbody>
      </table>

      {/* V3 */}
      <table className="table-auto border-collapse border border-gray-300 dark:border-gray-700 min-w-[220px] text-md">
        <thead>
          <tr>
            <th
              colSpan={4}
              className="border border-gray-300 dark:border-gray-700 px-3 py-2 text-center bg-gray-100 dark:bg-gray-800"
            >
              V3
            </th>
          </tr>
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-left">
              Level
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(n\\)"}</SafeMJ>
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(k\\)"}</SafeMJ>
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(n-k\\)"}</SafeMJ>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">L</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">70</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">55</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">15</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">M</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">70</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">44</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">26</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">Q</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">70</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">34</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">36</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">H</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">70</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">26</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">44</td>
          </tr>
        </tbody>
      </table>
    </div>

    <p className="mb-3">
      Depending on how long our message to encode is and how much redundancy we want to add, we'll choose one version and level or another.
      For example, our URL from before was made up of 23 bytes, so we'll definitely have to use a version higher than V1,
      since in that version <SafeMJ>{"\\(k<23\\)"}</SafeMJ> for all levels.
    </p>

    <p className="mb-3">
      Let's suppose, then, that we choose V2 and level M. Does it fit? Right, so to build the final message to encode we first need to add
      a <b>header</b> (<SafeMJ after="),">{"\\(H\\)"}</SafeMJ> which is nothing more than a series of initial bits indicating the number of bytes the message takes up and the mode (byte, numeric, kanji) used
      to obtain those bytes from the original symbols. This information is, as you can imagine, essential for the QR reader
      to be able to recover the text that was turned into bytes.
      
      For example, for our URL and version 2 it would be the following 12 bits:
      <SafeMJ scrollable>{"\\[H=\\underbrace{0100}_{\\displaystyle\\textnormal{byte mode}}\\underbrace{00010111}_{\\displaystyle 23}.\\]"}</SafeMJ>
      Finally, the message is padded with a <b>terminator</b> (<SafeMJ after=")">{"\\(T\\)"}</SafeMJ> of <SafeMJ after="'s">{"\\(0\\)"}</SafeMJ> (in our case, 4) so
      that the total sequence still has a number of bits that is a multiple of 8 and can therefore be regrouped into bytes.
      Look at our example:
      <SafeMJ scrollable>{`
        \\[
        \\underbrace{0100\\,0001\\mid0111}_{\\displaystyle H}\\,\\underbrace{0110\\mid1000}_{\\displaystyle\\texttt{h}}\\,\\underbrace{0111\\mid0100}_{\\displaystyle \\texttt{t}}\\,\\underbrace{0111\\mid0100}_{\\displaystyle \\texttt{t}}\\ldots\\underbrace{0110\\mid1101}_{\\displaystyle \\texttt{e}}\\,\\underbrace{0110\\mid0101}_{\\displaystyle \\texttt{s}}\\,\\underbrace{0000\\vphantom{\\mid}}_{\\displaystyle T},
        \\]
      `}</SafeMJ>
      or, if you prefer, in decimal:
      <SafeMJ scrollable>{`
        \\[
        65\\mid 118\\mid 135\\mid 71\\mid\\ldots\\mid 214\\mid 80.
        \\]
      `}</SafeMJ>
    In total you can see we get 25 bytes, so the V2-M configuration did indeed work for us, with <SafeMJ>{"\\(k-25=3\\)"}</SafeMJ> bytes to spare.
    These "extra" bytes, in fact, must also be filled in to reach the <SafeMJ>{"\\(k=28\\)"}</SafeMJ> bytes set by the level. In particular, the standard says to keep
    filling them in, always alternating with the following <b>padding</b> bytes:
    <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
      <li className="mb-1"><SafeMJ>{"\\(11101100_2=236,\\)"}</SafeMJ></li>
      <li className="mb-1"><SafeMJ>{"\\(00010001_2=17.\\)"}</SafeMJ></li>
    </ul>
    So, the final message of our URL becomes:
    <SafeMJ scrollable>{`
      \\[
      65\\mid 118\\mid 135\\mid 71\\mid\\ldots\\mid 214\\mid 80\\mid 236\\mid 17\\mid 236,
      \\]
    `}</SafeMJ>
    which in polynomial form is the following beast:
    <SafeMJ scrollable>{"\\[m(x)=65x^{27}+118x^{26}+135x^{25}+71x^{24}+\\ldots+214x^{4}+80x^{3}+236x^{2}+17x+236,\\]"}</SafeMJ>
    The coefficients, I insist, aren't ordinary integers, but representations of elements of <SafeMJ after=".">{"\\(\\mathbb{F}_{256}\\)"}</SafeMJ>{" "}
    Don't say I didn't warn you, eh... Also note the order of the bytes with respect to those coefficients: <SafeMJ>{"\\(m_i\\)"}</SafeMJ> isn't
    actually the <SafeMJ after="th">{"\\(i\\text{-}\\)"}</SafeMJ> byte, as would be natural, but the one at position <SafeMJ after=".">{"\\(k-1-i\\)"}</SafeMJ>{" "}
    This "inversion" means that, if the encoding is implemented with an LFSR (remember the previous section), the input symbols
    can be processed in the same order in which they're transmitted, without needing to store the whole message beforehand.
    Good old "streaming", basically. It obviously makes more sense for reading CDs or for communication systems than for a QR code,
    but hey, the standard sticks with this common practice anyway...
    </p>

    <p className="mb-3">
      Now, let's compute the exact redundancy by applying formula <SafeMJ after=".">{"\\((6.4)\\)"}</SafeMJ> You'll understand that doing the calculations by hand in our case is simply inhumane,
      so I've had to resort to a Python <a href="/qr_reed_solomon.py" download className="underline">script</a>. Download it if you like and
      have a look. It has a couple of interesting little tricks... Anyway, the result is the following 16 redundancy bytes:
      <SafeMJ scrollable>{`
        \\[
        203\\mid 190\\mid 51\\mid 143\\mid 221\\mid 26\\mid 162\\mid 71\\mid 122\\mid 177\\mid 35\\mid 100\\mid 214\\mid 116\\mid 191\\mid 221.
        \\]
      `}</SafeMJ>
      Appending them after the <SafeMJ>{"\\(28\\)"}</SafeMJ> bytes we already had, we finally complete
      the (inverted) codeword of length <SafeMJ after=",">{"\\(n=44\\)"}</SafeMJ> ready to be dumped into the QR matrix.
      But how do we do that last bit? That is, how do we build the final set of black and white little squares (<b>modules</b>)?
    </p>

    <p className="mb-3">
      Right, so the first thing to be clear about is where to place them; we'll see how later. It shouldn't surprise us that a full QR code
      contains more things besides the encoded message and the redundancy. Depending on the specific version, there will be some fixed modules (<b>patterns</b>)
      or others that help the QR reader scan the message. Some of the most typical ones are:
      <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1"><b>Finder pattern</b>: the legendary 3 big squares in the corners, which are used to locate and orient the QR code within the image.</li>
        <li className="mb-1"><b>Alignment pattern</b>: an extra reference point to correct perspective distortion.</li>
        <li className="mb-1"><b>Timing pattern</b>: a dashed line between the finders that lets the reader know the exact size of a module.</li>
        <li className="mb-1"><b>Format information</b>: all the additional configuration information the QR reader needs to know, such as the error-correction level used.</li>
      </ul>
      Here you have, for example, where those patterns are located in V2:
    </p>

    <img src={qr_anatomy} alt="Regions of a version 2 QR matrix" className="mx-auto my-4 max-w-[40rem] w-full" />

    <p className="mb-3">
      In larger versions things get a bit more complicated... Several alignment patterns appear, spread across the whole matrix
      (up to 46 in version 40!), and from version 7 onwards, a pattern with information about the QR version is added, which starts
      to come in handy as the relative size difference between one version and the next gets smaller and smaller.
    </p>

    <p className="mb-3">
      So, once we've defined and located our grey placement area, we can start filling it in. To do so, the idea is that each bit of
      the (inverted) word represents a little square: 1 = black and 0 = white, and we place them one by one in a <b>zigzag</b> pattern. Specifically, we
      start at the bottom-right corner of the matrix and move through pairs of columns, going all the way up to the top. On reaching the top,
      we move to the pair of columns immediately to the left and go back down to the bottom. And if along the way we run into a fixed module, that
      is, one taken by some pattern, we simply skip that position and move on to the next. And so on until they're all placed... A bit of a muddle, right?
      Better, otherwise, look at the specific case of version 2, which shows perfectly the little snake that forms:
    </p>

    <img src={qr_zigzag} alt="Zigzag path for placing bits in a version 2 QR matrix" className="mx-auto my-4 max-w-xl w-full" />

    <p className="mb-3">
      Moreover, if the message is so long that it can't be encoded with a single Reed-Solomon (remember the restriction <SafeMJ after="),">{"\\(n\\le q-1=255\\)"}</SafeMJ>{" "}
      then the data is split into several independent blocks, each with its own Reed-Solomon applied separately.
      Then, when placing them in the matrix, what's done is to interleave their bytes, that is, you write, for example: byte 1 of the first block, byte 1
      of the second block, byte 2 of the first block, byte 2 of the second block... like dealing cards.
      This interleaving is super useful, since if, for example, a coffee stain wipes out a specific area of the matrix, the damage is spread across
      several Reed-Solomon blocks instead of being concentrated all in one.
    </p>

    <p className="mb-3">
      Great, so there's just one more thing left. The last one, I promise. It turns out I've told you a half-truth, since the bit you see
      in each module of the QR isn't exactly the original "raw" bit. To get the final colour you
      see painted, you first have to pass it through one last filter: the <b>mask</b>, which applies to each non-fixed module of the matrix
      the following XOR operation:
      <SafeMJ scrollable>{"\\[\\text{final colour} = \\text{bit} \\oplus \\text{mask}(\\text{row},\\text{column}).\\]"}</SafeMJ>
    </p>

    <p className="mb-3">
      In total there are 8 possible masks, numbered from 0 to 7, each defined, as you can see, by a formula on the row and column occupied by
      each bit. For example, mask 0 inverts a module if the sum of its row and column is even. That is:
      <SafeMJ scrollable>{"\\[\\text{mask}_0 (i,j)=(i+j+1)\\bmod 2.\\]"}</SafeMJ>
      There are also more complicated formulas, of course. Look at this one for mask 6:
      <SafeMJ scrollable>{"\\[\\text{mask}_6 (i,j)=\\left((i\\cdot j)\\bmod 2+(i\\cdot j)\\bmod 3+1\\right)\\bmod 2.\\]"}</SafeMJ>
      Okay, but what's this for? Well, the idea is to prevent certain messages from generating problematic visual patterns, such as
      whole areas of identical modules, or shapes resembling fixed patterns that could confuse the QR reader.
      Let's say the mask statistically "scrambles" the data so that this doesn't happen. In fact, to choose which mask to use, the encoder
      tries all of them and finally decides based on a series of criteria such as: number of 2x2 blocks of the same colour, rows/columns
      with many identical modules in a row, etc.
    </p>

    <p className="mb-3">
      In our particular case of the URL and version V2-M, it turns out the winning mask is number 6. Let's see, then, how it applies to our first byte
      to place, <SafeMJ after=":">{"\\(65=01000001_2\\)"}</SafeMJ>
    </p>

    <div className="overflow-x-auto my-5">
      <table className="table-auto sm:table-fixed border-collapse border border-gray-300 dark:border-gray-700 w-full text-md mx-auto">
        <thead className="bg-gray-100 dark:bg-gray-800">
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">#</th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">Row</th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">Column</th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">Bit</th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">Mask</th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">Final colour</th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900">
          <tr><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">0</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">24</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">24</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(0\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><span role="img" aria-label="dark" title="dark" className="inline-block w-4 h-4 align-middle border border-gray-400 bg-black" /></td></tr>
          <tr><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">1</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">24</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">23</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><span role="img" aria-label="light" title="light" className="inline-block w-4 h-4 align-middle border border-gray-400 bg-white" /></td></tr>
          <tr><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">2</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">23</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">24</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(0\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><span role="img" aria-label="dark" title="dark" className="inline-block w-4 h-4 align-middle border border-gray-400 bg-black" /></td></tr>
          <tr><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">3</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">23</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">23</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(0\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><span role="img" aria-label="dark" title="dark" className="inline-block w-4 h-4 align-middle border border-gray-400 bg-black" /></td></tr>
          <tr><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">4</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">22</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">24</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(0\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><span role="img" aria-label="dark" title="dark" className="inline-block w-4 h-4 align-middle border border-gray-400 bg-black" /></td></tr>
          <tr><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">5</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">22</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">23</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(0\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><span role="img" aria-label="dark" title="dark" className="inline-block w-4 h-4 align-middle border border-gray-400 bg-black" /></td></tr>
          <tr><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">6</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">21</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">24</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(0\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><span role="img" aria-label="dark" title="dark" className="inline-block w-4 h-4 align-middle border border-gray-400 bg-black" /></td></tr>
          <tr><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">7</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">21</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">23</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(0\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><span role="img" aria-label="dark" title="dark" className="inline-block w-4 h-4 align-middle border border-gray-400 bg-black" /></td></tr>
        </tbody>
      </table>
    </div>

    <p className="mb-3">
      You simply have to repeat this process with all the bytes, place them following the zigzag we saw, add the pattern information
      and... done! We've got our QR code put together. Try scanning it below, you'll see the URL of this website come up.
    </p>

    <p className="mb-3">
      In fact, I took the chance to check that the theory we've developed in this article works,
      by deliberately modifying 8 of the message bytes. In particular, by "staining" (turning black) the ones at positions 15, 16, 21, 22, 27, 28, 33 and 34.
      Since in our case <SafeMJ after=",">{"\\(t=\\lfloor\\tfrac{n-k}{2}\\rfloor = 8\\)"}</SafeMJ> the reader is perfectly capable of recovering the text.
      But if we stain just one more byte (number 32), we go over the limit and the reader is indeed unable to scan it... Brilliant!
    </p>

    <div className="flex flex-wrap justify-center items-start gap-10 sm:gap-12 my-6">
      {renderQR(
        QR_ORIG,
        <>Original QR code (V2-M) of <b>https://jaimegimillo.me</b></>,
        "Original QR code (V2-M) of https://jaimegimillo.me"
      )}
      {renderQR(
        QR_DMG,
        <>The same QR code with 8 bytes altered. Try it, it still works!</>,
        "The same QR code with 8 bytes altered. Try it, it still works!",
        QR_DIFF
      )}
      {renderQR(
        QR_DMG_9,
        <>With 9 bytes altered, one more <br/> than <SafeMJ after=",">{"\\(t\\)"}</SafeMJ> it can no longer be read.</>,
        "With 9 bytes altered, one more than t, it can no longer be read.",
        QR_DIFF_9
      )}
    </div>
    </>
  );
}
